import React, { useEffect, useState, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { shiftAssignmentsAPI, employeesAPI, shiftsAPI, employeeDayOffPatternsAPI, operationsAPI, timesheetsAPI, departmentsAPI, QUERY_KEYS } from "@/api";
import { Card, CardContent, CardHeader, CardTitle, Button, Input, Badge, Modal } from "@/components/ui";
import { LoadingSpinner, Pagination } from "@/components/common";
import { Calendar, Plus, Edit2, Trash2, Clock, User, Infinity, Info, Users, List, CalendarDays, ChevronLeft, ChevronRight, ChevronDown, ChevronUp, Save, ShieldCheck, X, Search, Copy, CopyPlus, Check, ArrowRight, Filter, LogOut, Palette, RotateCcw } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { extractPaginatedData, paginateClientData } from "@/utils/pagination";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";

// Sana formatlovchi (YYYY-MM-DD → "1-apr, 2026")
const fmtDate = (v?: string | null) => {
  if (!v) return null;
  const d = new Date(v);
  if (isNaN(d.getTime())) return v.slice(0, 10);
  return d.toLocaleDateString("uz-UZ", { day: "numeric", month: "short", year: "numeric" });
};

// Input uchun YYYY-MM-DD formati
const toDateInput = (v?: string | null): string => {
  if (!v) return "";
  const d = new Date(v);
  if (isNaN(d.getTime())) return String(v).slice(0, 10);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

// Дни недели (0=Вс, 1=Пн, ..., 6=Сб)
// Кнопка «Копия на след. неделю» временно скрыта (поставить true, чтобы вернуть)
const SHOW_COPY_NEXT_WEEK: boolean = false;

const DAY_NAMES = ["Вс", "Пн", "Вт", "Ср", "Чт", "Пт", "Сб"];
const DAY_NAMES_FULL = ["Воскресенье", "Понедельник", "Вторник", "Среда", "Четверг", "Пятница", "Суббота"];
const RU_MONTH_SHORT_FULL = ['янв','фев','мар','апр','мая','июн','июл','авг','сен','окт','ноя','дек'];

/** ISO 8601 week number */
const getIsoWeekNumber = (date: Date): number => {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const dayNum = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  return Math.ceil((((d.getTime() - yearStart.getTime()) / 86400000) + 1) / 7);
};

/** Format week range like "28 апр – 3 мая 2026" */
const formatWeekRange = (mondayStr: string): { range: string; weekNum: number; year: number } => {
  const start = new Date(mondayStr + 'T00:00:00');
  const end = new Date(start);
  end.setDate(end.getDate() + 6);
  const sd = start.getDate();
  const sm = RU_MONTH_SHORT_FULL[start.getMonth()];
  const ed = end.getDate();
  const em = RU_MONTH_SHORT_FULL[end.getMonth()];
  const year = end.getFullYear();
  const range = start.getMonth() === end.getMonth()
    ? `${sd}–${ed} ${sm} ${year}`
    : `${sd} ${sm} – ${ed} ${em} ${year}`;
  return { range, weekNum: getIsoWeekNumber(start), year };
};

/** Get monday of the week containing today + optional week offset */
const getWeekMonday = (offset = 0) => {
  const d = new Date();
  const day = d.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  d.setDate(d.getDate() + diff + offset * 7);
  return toDateInput(d.toISOString());
};

/** Get 7 dates starting from monday string */
const getWeekDates = (monday: string): string[] => {
  const dates: string[] = [];
  const d = new Date(monday + 'T00:00:00');
  for (let i = 0; i < 7; i++) {
    const dd = new Date(d);
    dd.setDate(d.getDate() + i);
    dates.push(toDateInput(dd.toISOString()));
  }
  return dates;
};

const SHIFT_COLOR_STORAGE_KEY = 'faceid.shift-assignment.colors.v1';
const DISPLAY_COLOR_STORAGE_KEY = 'faceid.shift-assignment.display-colors.v1';
const DEFAULT_SHIFT_COLORS = ['#2563eb', '#16a34a', '#7c3aed', '#e11d48', '#0ea5e9', '#4338ca', '#65a30d', '#c026d3', '#dc2626', '#0f766e'];
const DISPLAY_COLOR_KEYS = ['day_off', 'empty'] as const;

type DisplayColorKey = (typeof DISPLAY_COLOR_KEYS)[number];

const DEFAULT_DISPLAY_COLORS: Record<DisplayColorKey, string> = {
  day_off: '#f59e0b',
  empty: '#94a3b8',
};

const DISPLAY_COLOR_META: Record<DisplayColorKey, { label: string; hint: string }> = {
  day_off: { label: 'Выходной', hint: 'День отдыха' },
  empty: { label: 'Пустая ячейка', hint: 'Без назначения' },
}; 

type ShiftPalette = {
  accent: string;
  background: string;
  softBackground: string;
  border: string;
  text: string;
  mutedText: string;
  ring: string;
};

type CalendarBadge = {
  key: string;
  label: string;
  className?: string;
  style?: React.CSSProperties;
  title?: string;
};

type CalendarStatusTone = {
  label: string;
  className?: string;
  style?: React.CSSProperties;
};

const normalizeHexColor = (value?: string | null): string | null => {
  if (!value) return null;
  const normalized = value.trim().replace(/^#/, '');
  if (!/^[0-9a-fA-F]{6}$/.test(normalized)) return null;
  return `#${normalized.toLowerCase()}`;
};

const hexToRgb = (hex: string): [number, number, number] => {
  const normalized = normalizeHexColor(hex) ?? '#2563eb';
  return [
    parseInt(normalized.slice(1, 3), 16),
    parseInt(normalized.slice(3, 5), 16),
    parseInt(normalized.slice(5, 7), 16),
  ];
};

const rgba = (hex: string, alpha: number): string => {
  const [red, green, blue] = hexToRgb(hex);
  return `rgba(${red}, ${green}, ${blue}, ${alpha})`;
};

const buildShiftPalette = (hex: string): ShiftPalette => ({
  accent: hex,
  background: rgba(hex, 0.16),
  softBackground: rgba(hex, 0.09),
  border: rgba(hex, 0.26),
  text: '#0f172a',
  mutedText: rgba(hex, 0.85),
  ring: rgba(hex, 0.18),
});

const buildBadgeStyle = (palette: ShiftPalette): React.CSSProperties => ({
  backgroundColor: palette.softBackground,
  borderColor: palette.border,
  color: palette.mutedText,
});

export default function ShiftAssignmentsPage() {
  const perPage = 15;
  const { can } = useAuth();
  const navigate = useNavigate();
  const canManage = can("manage_shifts");
  const [viewMode, setViewMode] = useState<"list" | "calendar">("calendar");
  const [page, setPage] = useState(1);
  const [showForm, setShowForm] = useState(false);
  const [showBulkForm, setShowBulkForm] = useState(false);
  const [editingAssignment, setEditingAssignment] = useState<any>(null);
  const [expandedEmployeeIds, setExpandedEmployeeIds] = useState<Set<number>>(new Set());
  const [filters, setFilters] = useState({ employee_id: "", department_id: "", from_date: "", to_date: "" });
  const queryClient = useQueryClient();

  const { data: assignmentsData, isLoading } = useQuery({
    queryKey: [...QUERY_KEYS.shiftAssignments(), filters],
    queryFn: () => shiftAssignmentsAPI.list({ ...filters }),
    // per_page sent via query param if API supports it
  });

  const { data: employeesData } = useQuery({
    queryKey: QUERY_KEYS.employees(),
    queryFn: () => employeesAPI.list({ per_page: 1000 }),
  });

  const { data: departmentsData } = useQuery({
    queryKey: [...QUERY_KEYS.departments(), { per_page: 200 }],
    queryFn: () => departmentsAPI.list({ per_page: 200 }),
  });

  const { data: shiftsData } = useQuery({
    queryKey: QUERY_KEYS.shifts(),
    queryFn: () => shiftsAPI.list(),
  });

  const { data: dayOffPatternsData } = useQuery({
    queryKey: QUERY_KEYS.employeeDayOffPatterns(),
    queryFn: () => employeeDayOffPatternsAPI.list({ is_active: "true" }),
  });

  const createMutation = useMutation({
    mutationFn: (data: any) => shiftAssignmentsAPI.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.shiftAssignments() });
      setShowForm(false);
      toast.success("Смена назначена!");
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: any }) => shiftAssignmentsAPI.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.shiftAssignments() });
      setEditingAssignment(null);
      toast.success("Обновлено!");
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => shiftAssignmentsAPI.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.shiftAssignments() });
      toast.success("Удалено");
    },
  });

  const bulkMutation = useMutation({
    mutationFn: (data: Parameters<typeof shiftAssignmentsAPI.bulkAssign>[0]) =>
      shiftAssignmentsAPI.bulkAssign(data),
    onSuccess: (res: any) => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.shiftAssignments() });
      setShowBulkForm(false);
      const { created = 0, updated = 0, skipped = 0 } = res?.data || {};
      toast.success(`Массовое назначение: создано ${created}, обновлено ${updated}, пропущено ${skipped}`);
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.message || "Произошла ошибка");
    },
  });

  const { items: assignments } = extractPaginatedData<any>(assignmentsData, page, perPage);
  const employees = Array.isArray(employeesData?.data)
    ? employeesData.data
    : employeesData?.data?.data || [];
  const departments = Array.isArray(departmentsData?.data)
    ? departmentsData.data
    : departmentsData?.data?.data || [];
  const shifts = Array.isArray(shiftsData?.data)
    ? shiftsData.data
    : shiftsData?.data?.data || [];
  const dayOffPatterns = Array.isArray(dayOffPatternsData?.data)
    ? dayOffPatternsData.data
    : dayOffPatternsData?.data?.data || [];

  const employeesForListFilter = useMemo(() => {
    if (!filters.department_id) return employees;
    return employees.filter((emp: any) => String(emp.department?.id ?? '') === String(filters.department_id));
  }, [employees, filters.department_id]);

  const groupedAssignments = useMemo(() => {
    const getTypeLabel = (assignment: any) => {
      if (assignment.date_from && !assignment.date_to && !assignment.assigned_date) return 'Постоянная';
      if (assignment.date_from && assignment.date_to) return 'Период';
      if (!assignment.date_from && assignment.assigned_date) return 'Разовая';
      if (assignment.day_of_week != null && !assignment.date_from && !assignment.assigned_date) return 'Еженедельная';
      return 'Назначение';
    };

    const getSortValue = (assignment: any) => {
      const raw = assignment.assigned_date || assignment.date_from || assignment.created_at || 0;
      const parsed = new Date(raw).getTime();
      return Number.isFinite(parsed) ? parsed : 0;
    };

    const grouped = new Map<number, any>();

    assignments.forEach((assignment: any) => {
      const employeeId = Number(assignment.employee_id ?? assignment.employee?.id ?? 0);
      if (!employeeId) return;

      if (!grouped.has(employeeId)) {
        grouped.set(employeeId, {
          employeeId,
          employeeName: assignment.employee?.full_name || '–',
          employeeCode: assignment.employee?.employee_id || '',
          departmentName: assignment.employee?.department?.name || '',
          items: [],
        });
      }

      grouped.get(employeeId).items.push(assignment);
    });

    return Array.from(grouped.values())
      .map((group) => {
        const items = [...group.items].sort((a, b) => getSortValue(b) - getSortValue(a));
        const typeCounts = items.reduce((acc: Record<string, number>, item: any) => {
          const label = getTypeLabel(item);
          acc[label] = (acc[label] ?? 0) + 1;
          return acc;
        }, {});

        return {
          ...group,
          items,
          activeCount: items.filter((item: any) => item.is_active).length,
          latestAssignment: items[0] ?? null,
          shiftPreview: items.slice(0, 3),
          typeCounts,
        };
      })
      .sort((a, b) => a.employeeName.localeCompare(b.employeeName));
  }, [assignments]);

  const { items: pagedAssignmentGroups, meta: groupedAssignmentsMeta } = useMemo(
    () => paginateClientData(groupedAssignments, page, perPage),
    [groupedAssignments, page, perPage],
  );

  const toggleExpandedEmployee = (employeeId: number) => {
    setExpandedEmployeeIds((prev) => {
      const next = new Set(prev);
      if (next.has(employeeId)) {
        next.delete(employeeId);
      } else {
        next.add(employeeId);
      }
      return next;
    });
  };

  const resetExpandedEmployees = () => {
    setExpandedEmployeeIds(new Set());
  };

  const handleViewModeChange = (nextMode: "list" | "calendar") => {
    setViewMode(nextMode);
    resetExpandedEmployees();
  };

  const handleFilterChange = (changes: Partial<typeof filters>) => {
    setPage(1);
    resetExpandedEmployees();
    setFilters((current) => ({ ...current, ...changes }));
  };

  const handleDelete = (id: number) => {
    if (window.confirm("Удалить назначение?")) {
      deleteMutation.mutate(id);
    }
  };

  if (isLoading) return <LoadingSpinner />;

  return (
    <div className="space-y-6 page-shell">
      {/* Header */}
      <div className="page-toolbar">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Назначение смен</h1>
          <p className="text-slate-500 mt-1">Назначьте сотрудникам ежемесячную или постоянную смену</p>
        </div>
        <div className="flex items-center gap-2">
          {/* View toggle */}
          <div className="flex rounded-xl border border-slate-200 overflow-hidden">
            <button
              onClick={() => handleViewModeChange("calendar")}
              className={`px-3 py-2 text-sm flex items-center gap-1.5 transition ${
                viewMode === "calendar" ? "bg-blue-600 text-white" : "bg-white text-slate-600 hover:bg-slate-50"
              }`}
            >
              <CalendarDays className="w-4 h-4" /> Календарь
            </button>
            <button
              onClick={() => handleViewModeChange("list")}
              className={`px-3 py-2 text-sm flex items-center gap-1.5 transition ${
                viewMode === "list" ? "bg-blue-600 text-white" : "bg-white text-slate-600 hover:bg-slate-50"
              }`}
            >
              <List className="w-4 h-4" /> Список
            </button>
          </div>
          {canManage && (
            <>
              <Button variant="outline" onClick={() => navigate('/employee-day-offs')}>
                <Calendar className="w-4 h-4 mr-2" />
                Выходные
              </Button>
              <Button variant="outline" onClick={() => setShowBulkForm(true)}>
                <Users className="w-4 h-4 mr-2" />
                Массово
              </Button>
              <Button onClick={() => { setEditingAssignment(null); setShowForm(true); }}>
                <Plus className="w-4 h-4 mr-2" />
                Назначить
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Info box */}
      {/* <div className="rounded-xl border border-blue-100 bg-blue-50 px-4 py-3 flex items-start gap-3 text-sm text-blue-800">
        <Info className="w-4 h-4 mt-0.5 shrink-0 text-blue-500" />
        <div>
          <strong>Как это работает?</strong>
          <ul className="list-disc pl-4 mt-1 space-y-0.5">
            <li><strong>Ежемесячная / постоянная</strong> — задайте дату «Начало» и необязательно «Окончание». Если «Окончание» пусто, смена действует до отмены (∞).</li>
            <li><strong>Разовое исключение</strong> — действует только в выбранный день и не отменяет основную смену.</li>
            <li><strong>Личный выходной</strong> — ведётся на странице <em>Выходные</em> (просмотр / редактирование / удаление). В этот день система не ожидает check-in и статус будет <em>Выходной</em>.</li>
            <li><strong>Выходные дни</strong> — задаются в рабочем календаре филиала (Work Calendar). Если отмечены <em>суббота и воскресенье</em>, система автоматически ставит статус «Выходной».</li>
          </ul>
        </div>
      </div> */}

      {/* Calendar View */}
      {viewMode === "calendar" && (
        <CalendarView
          employees={employees}
          departments={departments}
          shifts={shifts}
          canManage={canManage}
        />
      )}

      {/* Filters */}
      {viewMode === "list" && (
      <Card>
        <CardContent className="py-4">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
            <div>
              <label className="text-sm font-medium text-slate-600 block mb-1">Сотрудник</label>
              <select
                className="w-full min-h-[2.75rem] rounded-xl border border-gray-300 px-4 py-2 text-sm"
                value={filters.employee_id}
                onChange={(e) => handleFilterChange({ employee_id: e.target.value })}
              >
                <option value="">Все сотрудники</option>
                {employeesForListFilter.map((emp: any) => (
                  <option key={emp.id} value={emp.id}>{emp.full_name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-600 block mb-1">Отдел</label>
              <select
                className="w-full min-h-[2.75rem] rounded-xl border border-gray-300 px-4 py-2 text-sm"
                value={filters.department_id}
                onChange={(e) => handleFilterChange({ department_id: e.target.value, employee_id: '' })}
              >
                <option value="">Все отделы</option>
                {departments.map((dept: any) => (
                  <option key={dept.id} value={dept.id}>{dept.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-600 block mb-1">С даты</label>
              <Input type="date" value={filters.from_date}
                onChange={(e) => handleFilterChange({ from_date: e.target.value })} />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-600 block mb-1">По дату</label>
              <Input type="date" value={filters.to_date}
                onChange={(e) => handleFilterChange({ to_date: e.target.value })} />
            </div>
          </div>
        </CardContent>
      </Card>
      )}

      {/* Modal */}
      <Modal
        isOpen={showForm || !!editingAssignment}
        onClose={() => { setShowForm(false); setEditingAssignment(null); }}
        title={editingAssignment ? "Редактировать назначение" : "Новое назначение"}
      >
        <AssignmentForm
          initialData={editingAssignment}
          employees={employees}
          shifts={shifts}
          dayOffPatterns={dayOffPatterns}
          onSubmit={async ({ assignmentData, dayOffPattern }) => {
            if (editingAssignment) {
              await updateMutation.mutateAsync({ id: editingAssignment.id, data: assignmentData });
            } else {
              await createMutation.mutateAsync(assignmentData);
            }

            if (dayOffPattern) {
              await employeeDayOffPatternsAPI.create({
                employee_id: assignmentData.employee_id,
                weekdays: dayOffPattern.weekdays,
                reason: dayOffPattern.reason,
                is_paid: dayOffPattern.is_paid,
                effective_from: dayOffPattern.effective_from,
                is_active: true,
              });

              queryClient.invalidateQueries({ queryKey: QUERY_KEYS.employeeDayOffPatterns() });
              queryClient.invalidateQueries({ queryKey: QUERY_KEYS.timesheets() });
              toast.success("Смена сохранена вместе с расписанием выходных");
            }
          }}
          onCancel={() => { setShowForm(false); setEditingAssignment(null); }}
        />
      </Modal>

      {/* Bulk Assignment Modal */}
      <Modal
        isOpen={showBulkForm}
        onClose={() => setShowBulkForm(false)}
        title="Массовое назначение смен"
      >
        <BulkAssignmentForm
          employees={employees}
          shifts={shifts}
          onSubmit={(data) => bulkMutation.mutate(data)}
          onCancel={() => setShowBulkForm(false)}
          isLoading={bulkMutation.isPending}
        />
      </Modal>

      {/* Table */}
      {viewMode === "list" && (<>
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 border-b">
                <tr>
                  <th className="text-left py-3 px-4 font-semibold text-slate-700">Сотрудник</th>
                  <th className="text-left py-3 px-4 font-semibold text-slate-700">Смена</th>
                  <th className="text-left py-3 px-4 font-semibold text-slate-700">Период</th>
                  <th className="text-left py-3 px-4 font-semibold text-slate-700">Тип</th>
                  <th className="text-left py-3 px-4 font-semibold text-slate-700">Статус</th>
                  {canManage && <th className="text-left py-3 px-4 font-semibold text-slate-700">Действия</th>}
                </tr>
              </thead>
              <tbody>
                {pagedAssignmentGroups.map((group: any) => {
                  const isExpanded = expandedEmployeeIds.has(group.employeeId);
                  const latest = group.latestAssignment;
                  const latestDate = latest
                    ? fmtDate(latest.assigned_date || latest.date_from || latest.created_at)
                    : '—';

                  return (
                    <React.Fragment key={group.employeeId}>
                      <tr className="border-b hover:bg-slate-50/70 transition">
                        <td className="py-3 px-4 align-top">
                          <div className="flex items-start gap-2.5">
                            <div className="mt-0.5 flex h-8 w-8 items-center justify-center rounded-full bg-slate-100 text-xs font-semibold text-slate-600">
                              {(group.employeeName ?? '?')[0]}
                            </div>
                            <div>
                              <div className="font-medium text-slate-800">{group.employeeName}</div>
                              <div className="mt-1 flex flex-wrap items-center gap-1.5 text-xs text-slate-400">
                                {group.employeeCode && <span>{group.employeeCode}</span>}
                                {group.departmentName && (
                                  <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-medium text-slate-500">
                                    {group.departmentName}
                                  </span>
                                )}
                                <span className="rounded-full bg-blue-50 px-2 py-0.5 text-[10px] font-semibold text-blue-600">
                                  {group.items.length} зап.
                                </span>
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="py-3 px-4 align-top">
                          <div className="flex flex-wrap gap-2">
                            {group.shiftPreview.map((item: any) => (
                              <div key={item.id} className="rounded-xl border border-slate-200 bg-slate-50 px-2.5 py-1.5">
                                <div className="text-xs font-semibold text-slate-700">{item.shift?.name || '—'}</div>
                                <div className="text-[10px] text-slate-400 font-mono">
                                  {item.shift?.start_time?.slice(0, 5)} – {item.shift?.end_time?.slice(0, 5)}
                                </div>
                              </div>
                            ))}
                            {group.items.length > group.shiftPreview.length && (
                              <div className="rounded-xl border border-dashed border-slate-200 bg-white px-2.5 py-1.5 text-xs font-medium text-slate-500">
                                +{group.items.length - group.shiftPreview.length} ещё
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="py-3 px-4 align-top">
                          <div className="text-sm font-medium text-slate-700">Последнее: {latestDate}</div>
                          <div className="mt-1 text-xs text-slate-400">Все назначения одного сотрудника</div>
                        </td>
                        <td className="py-3 px-4 align-top">
                          <div className="flex flex-wrap gap-1.5">
                            {Object.entries(group.typeCounts).map(([label, count]) => (
                              <span key={label} className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-medium text-slate-600">
                                {label}: {String(count)}
                              </span>
                            ))}
                          </div>
                        </td>
                        <td className="py-3 px-4 align-top">
                          <div className="flex flex-col gap-1">
                            <Badge variant={group.activeCount > 0 ? "success" : "secondary"}>
                              {group.activeCount} актив.
                            </Badge>
                            <span className="text-xs text-slate-400">Всего: {group.items.length}</span>
                          </div>
                        </td>
                        {canManage && (
                          <td className="py-3 px-4 align-top">
                            <Button size="sm" variant="outline" onClick={() => toggleExpandedEmployee(group.employeeId)}>
                              {isExpanded ? <ChevronUp className="w-4 h-4 mr-1" /> : <ChevronDown className="w-4 h-4 mr-1" />}
                              {isExpanded ? 'Свернуть' : 'Показать'}
                            </Button>
                          </td>
                        )}
                      </tr>

                      {isExpanded && (
                        <tr className="border-b bg-slate-50/60">
                          <td colSpan={canManage ? 6 : 5} className="px-4 py-4">
                            <div className="grid gap-3">
                              {group.items.map((a: any) => {
                                const isIndefinite = a.date_from && !a.date_to && !a.assigned_date;
                                const isRange = a.date_from && a.date_to;
                                const isSingle = !a.date_from && a.assigned_date;
                                const isDow = a.day_of_week != null && !a.date_from && !a.assigned_date;

                                return (
                                  <div key={a.id} className="rounded-2xl border border-slate-200 bg-white px-4 py-3 shadow-sm">
                                    <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                                      <div className="flex items-center gap-3">
                                        <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-blue-50 text-blue-600">
                                          <Clock className="w-4 h-4" />
                                        </div>
                                        <div>
                                          <div className="font-semibold text-slate-900">{a.shift?.name || '—'}</div>
                                          <div className="text-xs text-slate-400 font-mono">
                                            {a.shift?.start_time?.slice(0, 5)} – {a.shift?.end_time?.slice(0, 5)}
                                          </div>
                                        </div>
                                      </div>

                                      <div className="flex flex-wrap items-center gap-2 text-sm text-slate-600">
                                        {isIndefinite && (
                                          <span>{fmtDate(a.date_from)} → ∞</span>
                                        )}
                                        {isRange && (
                                          <span>{fmtDate(a.date_from)} → {fmtDate(a.date_to)}</span>
                                        )}
                                        {isSingle && (
                                          <span>{fmtDate(a.assigned_date)}</span>
                                        )}
                                        {isIndefinite && <Badge variant="default">Постоянная</Badge>}
                                        {isRange && <Badge variant="secondary">Периодическая</Badge>}
                                        {isSingle && <Badge variant="outline">Разовая</Badge>}
                                        {isDow && <Badge variant="secondary">Еженедельная</Badge>}
                                        <Badge variant={a.is_active ? "success" : "secondary"}>
                                          {a.is_active ? "Активна" : "Неактивна"}
                                        </Badge>
                                      </div>

                                      {canManage && (
                                        <div className="flex gap-2">
                                          <Button size="sm" variant="outline" onClick={() => { setShowForm(false); setEditingAssignment(a); }}>
                                            <Edit2 className="w-4 h-4" />
                                          </Button>
                                          <Button size="sm" variant="outline" onClick={() => handleDelete(a.id)}>
                                            <Trash2 className="w-4 h-4 text-red-600" />
                                          </Button>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      <Pagination meta={groupedAssignmentsMeta} onPageChange={setPage} />
      </>)}

      {groupedAssignments.length === 0 && viewMode === "list" && (
        <div className="text-center py-12">
          <Calendar className="w-16 h-16 mx-auto text-slate-400 mb-4" />
          <h3 className="text-lg font-medium text-slate-800">Tayinlashlar topilmadi</h3>
          <p className="text-slate-500">Нажмите кнопку «Назначить смену» выше.</p>
        </div>
      )}
    </div>
  );
}

// ─── Form ─────────────────────────────────────────────────────────────────────

function AssignmentForm({
  initialData,
  employees,
  shifts,
  dayOffPatterns,
  onSubmit,
  onCancel,
}: {
  initialData?: any;
  employees: any[];
  shifts: any[];
  dayOffPatterns: any[];
  onSubmit: (data: {
    assignmentData: any;
    dayOffPattern: {
      weekdays: number[];
      reason: string;
      is_paid?: boolean;
      effective_from?: string | null;
    } | null;
  }) => void;
  onCancel: () => void;
}) {
  const initialPattern = dayOffPatterns.find(
    (pattern: any) => String(pattern.employee_id) === String(initialData?.employee_id ?? '') && pattern.is_active !== false,
  );

  // Determine initial mode
  const getInitialMode = (): "permanent" | "range" | "single" => {
    if (initialData?.assigned_date && !initialData?.date_from) return "single";
    if (initialData?.date_from && initialData?.date_to) return "range";
    if (initialData?.date_from && !initialData?.date_to) return "permanent";
    return "permanent"; // default: monthly/indefinite
  };

  const [mode, setMode] = useState<"permanent" | "range" | "single">(getInitialMode);
  const [empId, setEmpId] = useState(String(initialData?.employee_id ?? ""));
  const [shiftId, setShiftId] = useState(String(initialData?.shift_id ?? ""));
  const [dateFrom, setDateFrom] = useState(toDateInput(initialData?.date_from));
  const [dateTo, setDateTo] = useState(toDateInput(initialData?.date_to));
  const [singleDate, setSingleDate] = useState(toDateInput(initialData?.assigned_date ?? initialData?.date_from));
  const [isActive, setIsActive] = useState(initialData?.is_active ?? true);
  const [attachDayOffs, setAttachDayOffs] = useState(Boolean(initialPattern));
  const [planWeekdays, setPlanWeekdays] = useState<number[]>(Array.isArray(initialPattern?.weekdays) ? initialPattern.weekdays : []);
  const [planMonth, setPlanMonth] = useState(initialPattern?.effective_from ? String(initialPattern.effective_from).slice(0, 7) : "");
  const [planReason, setPlanReason] = useState(initialPattern?.reason || "");
  const [planPaid, setPlanPaid] = useState(Boolean(initialPattern?.is_paid));

  const handleEmployeeChange = (value: string) => {
    setEmpId(value);

    if (!value) {
      setAttachDayOffs(false);
      setPlanWeekdays([]);
      setPlanReason("");
      setPlanPaid(false);
      setPlanMonth("");
      return;
    }

    const pattern = dayOffPatterns.find((item: any) => String(item.employee_id) === value && item.is_active !== false);

    if (!pattern) {
      setAttachDayOffs(false);
      setPlanWeekdays([]);
      setPlanReason("");
      setPlanPaid(false);
      setPlanMonth("");
      return;
    }

    setAttachDayOffs(true);
    setPlanWeekdays(Array.isArray(pattern.weekdays) ? pattern.weekdays : []);
    setPlanReason(pattern.reason || "");
    setPlanPaid(Boolean(pattern.is_paid));
    setPlanMonth(pattern.effective_from ? String(pattern.effective_from).slice(0, 7) : "");
  };

  const togglePlanWeekday = (day: number) => {
    setPlanWeekdays((prev) => (prev.includes(day) ? prev.filter((d) => d !== day) : [...prev, day]));
  };

  const handleSubmit = () => {
    if (!empId || !shiftId) { toast.warning("Выберите сотрудника и смену"); return; }

    const base: any = { employee_id: Number(empId), shift_id: Number(shiftId), is_active: isActive };

    if (mode === "single") {
      if (!singleDate) { toast.warning("Введите дату"); return; }
      base.assigned_date = singleDate;
      base.date_from = singleDate;
      base.date_to = singleDate;
    } else if (mode === "range") {
      if (!dateFrom || !dateTo) { toast.warning("Введите обе даты"); return; }
      base.date_from = dateFrom;
      base.date_to = dateTo;
    } else {
      // permanent
      if (!dateFrom) { toast.warning("Введите дату начала"); return; }
      base.date_from = dateFrom;
      base.date_to = null; // cheksiz
    }

    let dayOffPattern: {
      weekdays: number[];
      reason: string;
      is_paid?: boolean;
      effective_from?: string | null;
    } | null = null;

    if (attachDayOffs) {
      if (!planReason.trim()) {
        toast.warning("Введите причину выходных");
        return;
      }
      if (planWeekdays.length === 0) {
        toast.warning("Выберите хотя бы один день недели");
        return;
      }

      let effectiveFrom = planMonth ? `${planMonth}-01` : null;
      if (!effectiveFrom) {
        effectiveFrom = mode === "single" ? singleDate : dateFrom;
      }

      dayOffPattern = {
        weekdays: planWeekdays,
        reason: planReason.trim(),
        is_paid: planPaid,
        effective_from: effectiveFrom,
      };
    }

    onSubmit({ assignmentData: base, dayOffPattern });
  };

  const selClass = "w-full min-h-[2.75rem] rounded-xl border border-gray-300 px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500";
  const modeBtn = (m: typeof mode, label: string) => (
    <button
      type="button"
      onClick={() => setMode(m)}
      className={`flex-1 py-2 px-3 rounded-xl text-sm font-medium border transition-all ${
        mode === m
          ? "bg-blue-600 text-white border-blue-600"
          : "bg-white text-slate-600 border-slate-300 hover:border-blue-400"
      }`}
    >{label}</button>
  );

  return (
    <div className="space-y-4">
      {/* Employee */}
      <div>
        <label className="text-sm font-medium text-slate-600 block mb-1">Сотрудник *</label>
        <select className={selClass} value={empId} onChange={(e) => handleEmployeeChange(e.target.value)}>
          <option value="">Выберите сотрудника</option>
          {employees.map((emp: any) => (
            <option key={emp.id} value={emp.id}>
              {emp.full_name} {emp.employee_id ? `(${emp.employee_id})` : ""}
            </option>
          ))}
        </select>
      </div>

      {/* Shift */}
      <div>
        <label className="text-sm font-medium text-slate-600 block mb-1">Смена *</label>
        <select className={selClass} value={shiftId} onChange={(e) => setShiftId(e.target.value)}>
          <option value="">Выберите смену</option>
          {shifts.map((s: any) => (
            <option key={s.id} value={s.id}>
              {s.name} — {s.start_time?.slice(0, 5)}–{s.end_time?.slice(0, 5)}
            </option>
          ))}
        </select>
      </div>

      {/* Mode selector */}
      <div>
        <label className="text-sm font-medium text-slate-600 block mb-2">Тип назначения *</label>
        <div className="flex gap-2">
          {modeBtn("permanent", "♾ Постоянная")}
          {modeBtn("range", "📅 Период")}
          {modeBtn("single", "1️⃣ Разовая")}
        </div>
        <p className="text-xs text-slate-400 mt-1.5">
          {mode === "permanent" && "Даты окончания нет — смена действует до отмены. Выходные берутся из календаря автоматически."}
          {mode === "range" && "Действует в указанный период, выходные пропускаются."}
          {mode === "single" && "Действует только в этот день. Перекрывает постоянную смену."}
        </p>
      </div>

      {/* Date inputs */}
      {mode === "single" && (
        <div>
          <label className="text-sm font-medium text-slate-600 block mb-1">Дата *</label>
          <Input type="date" value={singleDate} onChange={(e) => setSingleDate(e.target.value)} />
        </div>
      )}

      {mode === "permanent" && (
        <div>
          <label className="text-sm font-medium text-slate-600 block mb-1">Дата начала *</label>
          <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
          <p className="text-xs text-slate-400 mt-1">Даты окончания нет (∞ бесконечно).</p>
        </div>
      )}

      {mode === "range" && (
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-sm font-medium text-slate-600 block mb-1">Начало *</label>
            <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
          </div>
          <div>
            <label className="text-sm font-medium text-slate-600 block mb-1">Окончание *</label>
            <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
          </div>
        </div>
      )}

      <div className="rounded-xl border border-slate-200 p-3 bg-slate-50/70 space-y-3">
        <label className="flex items-center gap-2 cursor-pointer select-none">
          <input
            type="checkbox"
            className="w-4 h-4 accent-blue-600"
            checked={attachDayOffs}
            onChange={(e) => setAttachDayOffs(e.target.checked)}
          />
          <span className="text-sm text-slate-700 font-medium">Вместе со сменой назначить выходные</span>
        </label>

        {attachDayOffs && (
          <div className="space-y-3">
            <div>
              <label className="text-sm font-medium text-slate-600 block mb-2">В какие дни недели выходные?</label>
              <div className="flex flex-wrap gap-2">
                {DAY_NAMES.map((d, idx) => (
                  <button
                    key={idx}
                    type="button"
                    onClick={() => togglePlanWeekday(idx)}
                    className={`px-3 py-1.5 rounded-lg border text-sm ${planWeekdays.includes(idx)
                      ? "bg-blue-600 text-white border-blue-600"
                      : "bg-white text-slate-600 border-slate-300"}`}
                  >
                    {d}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="text-sm font-medium text-slate-600 block mb-1">Месяц начала (необяз.)</label>
              <Input type="month" value={planMonth} onChange={(e) => setPlanMonth(e.target.value)} />
              <p className="text-xs text-slate-400 mt-1">Если пусто — смена начнёт действовать с даты начала.</p>
            </div>

            <div>
              <label className="text-sm font-medium text-slate-600 block mb-1">Причина выходных *</label>
              <textarea
                className="w-full min-h-[2.75rem] rounded-xl border border-gray-300 px-4 py-2 text-sm"
                value={planReason}
                onChange={(e) => setPlanReason(e.target.value)}
                placeholder="Например: выходной по графику смен"
              />
            </div>

            <label className="flex items-center gap-2 cursor-pointer select-none">
              <input
                type="checkbox"
                className="w-4 h-4 accent-blue-600"
                checked={planPaid}
                onChange={(e) => setPlanPaid(e.target.checked)}
              />
              <span className="text-sm text-slate-700">Эти выходные оплачиваемые</span>
            </label>
          </div>
        )}
      </div>

      {/* Active toggle */}
      <label className="flex items-center gap-2 cursor-pointer select-none">
        <input type="checkbox" className="w-4 h-4 accent-blue-600" checked={isActive}
          onChange={(e) => setIsActive(e.target.checked)} />
        <span className="text-sm text-slate-700">Активно</span>
      </label>

      {/* Actions */}
      <div className="flex gap-3 pt-1">
        <Button onClick={handleSubmit} className="flex-1">Сохранить</Button>
        <Button onClick={onCancel} variant="outline" className="flex-1">Отмена</Button>
      </div>
    </div>
  );
}

// ─── Bulk Assignment Form ─────────────────────────────────────────────────────

function BulkAssignmentForm({
  employees,
  shifts,
  onSubmit,
  onCancel,
  isLoading,
}: {
  employees: any[];
  shifts: any[];
  onSubmit: (data: Parameters<typeof shiftAssignmentsAPI.bulkAssign>[0]) => void;
  onCancel: () => void;
  isLoading?: boolean;
}) {
  const [shiftId, setShiftId] = useState("");
  const [selectedEmployees, setSelectedEmployees] = useState<number[]>([]);
  const [bulkMode, setBulkMode] = useState<"range" | "permanent">("range");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [overwrite, setOverwrite] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  const filteredEmployees = employees.filter((emp: any) =>
    emp.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    emp.employee_id?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const toggleEmployee = (id: number) => {
    setSelectedEmployees((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const selectAll = () => {
    setSelectedEmployees(filteredEmployees.map((e: any) => e.id));
  };

  const deselectAll = () => {
    setSelectedEmployees([]);
  };

  const handleSubmit = () => {
    if (!shiftId) {
      toast.warning("Выберите смену");
      return;
    }
    if (selectedEmployees.length === 0) {
      toast.warning("Выберите хотя бы одного сотрудника");
      return;
    }
    if (!dateFrom) {
      toast.warning("Введите дату начала");
      return;
    }
    if (bulkMode === "range" && !dateTo) {
      toast.warning("Введите дату окончания");
      return;
    }

    onSubmit({
      shift_id: Number(shiftId),
      employee_ids: selectedEmployees,
      date_from: dateFrom,
      date_to: bulkMode === "permanent" ? undefined : dateTo,
      overwrite_existing: overwrite,
    });
  };

  const selClass =
    "w-full min-h-[2.75rem] rounded-xl border border-gray-300 px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500";

  return (
    <div className="space-y-4">
      {/* Info */}
      <div className="rounded-xl border border-blue-100 bg-blue-50 px-4 py-3 text-sm text-blue-800">
        <strong>Массовое назначение</strong> — назначьте смену нескольким сотрудникам одновременно.
        Удобно, когда смены меняются каждую неделю.
      </div>

      {/* Shift */}
      <div>
        <label className="text-sm font-medium text-slate-600 block mb-1">Смена *</label>
        <select className={selClass} value={shiftId} onChange={(e) => setShiftId(e.target.value)}>
          <option value="">Выберите смену</option>
          {shifts.map((s: any) => (
            <option key={s.id} value={s.id}>
              {s.name} — {s.start_time?.slice(0, 5)}–{s.end_time?.slice(0, 5)}
            </option>
          ))}
        </select>
      </div>

      {/* Assignment mode */}
      <div>
        <label className="text-sm font-medium text-slate-600 block mb-2">Тип назначения</label>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => setBulkMode("range")}
            className={`flex-1 py-2 px-3 rounded-xl text-sm font-medium border transition-all ${
              bulkMode === "range"
                ? "bg-blue-600 text-white border-blue-600"
                : "bg-white text-slate-600 border-slate-300 hover:border-blue-400"
            }`}
          >📅 Период</button>
          <button
            type="button"
            onClick={() => setBulkMode("permanent")}
            className={`flex-1 py-2 px-3 rounded-xl text-sm font-medium border transition-all ${
              bulkMode === "permanent"
                ? "bg-blue-600 text-white border-blue-600"
                : "bg-white text-slate-600 border-slate-300 hover:border-blue-400"
            }`}
          >♾ Постоянная</button>
        </div>
      </div>

      {/* Date range */}
      <div className={bulkMode === "range" ? "grid grid-cols-2 gap-3" : ""}>
        <div>
          <label className="text-sm font-medium text-slate-600 block mb-1">Начало *</label>
          <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
        </div>
        {bulkMode === "range" && (
          <div>
            <label className="text-sm font-medium text-slate-600 block mb-1">Окончание *</label>
            <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
          </div>
        )}
        {bulkMode === "permanent" && (
          <p className="text-xs text-slate-400 mt-1">Даты окончания нет — смена действует до отмены (∞)</p>
        )}
      </div>

      {/* Employee selection */}
      <div>
        <label className="text-sm font-medium text-slate-600 block mb-1">
          Сотрудники * ({selectedEmployees.length} выбр.)
        </label>
        <Input
          placeholder="Поиск сотрудника..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="mb-2"
        />
        <div className="flex gap-2 mb-2">
          <Button type="button" variant="outline" size="sm" onClick={selectAll}>
            Выбрать всех
          </Button>
          <Button type="button" variant="outline" size="sm" onClick={deselectAll}>
            Снять выбор
          </Button>
        </div>
        <div className="max-h-48 overflow-y-auto border rounded-xl p-2 space-y-1">
          {filteredEmployees.map((emp: any) => (
            <label
              key={emp.id}
              className={`flex items-center gap-2 p-2 rounded-lg cursor-pointer transition ${
                selectedEmployees.includes(emp.id)
                  ? "bg-blue-50 border border-blue-200"
                  : "hover:bg-slate-50"
              }`}
            >
              <input
                type="checkbox"
                className="w-4 h-4 accent-blue-600"
                checked={selectedEmployees.includes(emp.id)}
                onChange={() => toggleEmployee(emp.id)}
              />
              <span className="text-sm text-slate-800">{emp.full_name}</span>
              {emp.employee_id && (
                <span className="text-xs text-slate-400">({emp.employee_id})</span>
              )}
            </label>
          ))}
          {filteredEmployees.length === 0 && (
            <div className="text-center text-sm text-slate-400 py-4">
              Сотрудники не найдены
            </div>
          )}
        </div>
      </div>

      {/* Overwrite toggle */}
      <label className="flex items-center gap-2 cursor-pointer select-none">
        <input
          type="checkbox"
          className="w-4 h-4 accent-blue-600"
          checked={overwrite}
          onChange={(e) => setOverwrite(e.target.checked)}
        />
        <span className="text-sm text-slate-700">
          Перезаписать существующие назначения
        </span>
      </label>

      {/* Actions */}
      <div className="flex gap-3 pt-1">
        <Button onClick={handleSubmit} className="flex-1" disabled={isLoading}>
          {isLoading ? "Сохранение..." : "Назначить"}
        </Button>
        <Button onClick={onCancel} variant="outline" className="flex-1" disabled={isLoading}>
          Отмена
        </Button>
      </div>
    </div>
  );
}

// ─── Calendar View (недельное управление) ─────────────────────────────────────

function CalendarView({
  employees,
  departments,
  shifts: parentShifts,
  canManage,
}: {
  employees: any[];
  departments: any[];
  shifts: any[];
  canManage: boolean;
}) {
  const queryClient = useQueryClient();
  const [weekStart, setWeekStart] = useState(getWeekMonday());
  const weekDates = useMemo(() => getWeekDates(weekStart), [weekStart]);

  // ── Local changes for batch save ──
  const [localChanges, setLocalChanges] = useState<Record<string, {
    employee_id: number;
    date: string;
    shift_id: number | null;
    is_day_off?: boolean;
    reason?: string | null;
  }>>({});

  // ── Search & filter ──
  const [search, setSearch] = useState('');
  const [filterDepartmentId, setFilterDepartmentId] = useState<string>('');
  const [filterShiftId, setFilterShiftId] = useState<string>('');

  // ── Excuse modal state ──
  const [excuseModal, setExcuseModal] = useState<{ open: boolean; empId: number; empName: string; date: string } | null>(null);
  const [excuseForm, setExcuseForm] = useState({ excused_until: '11:00', excuse_reason: '' });
  const [excuseLoading, setExcuseLoading] = useState(false);

  // ── Early leave excuse modal state ──
  const [earlyLeaveModal, setEarlyLeaveModal] = useState<{ open: boolean; empId: number; empName: string; date: string; hasExisting: boolean } | null>(null);
  const [earlyLeaveForm, setEarlyLeaveForm] = useState({ early_leave_excused_until: '16:00', early_leave_excuse_reason: '' });
  const [earlyLeaveLoading, setEarlyLeaveLoading] = useState(false);

  // ── Day off modal state ──
  const [dayOffModal, setDayOffModal] = useState<{ empId: number; empName: string; date: string } | null>(null);
  const [dayOffReason, setDayOffReason] = useState('');

  // ── Cell action modal ──
  const [activeCell, setActiveCell] = useState<{
    empId: number;
    empName: string;
    date: string;
    cell: any;
  } | null>(null);

  // Close modal on escape and lock body scroll
  useEffect(() => {
    if (!activeCell) return;
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') setActiveCell(null); };
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', esc);
    return () => {
      document.body.style.overflow = originalOverflow;
      document.removeEventListener('keydown', esc);
    };
  }, [activeCell]);

  // ── Copy employee modal ──
  const [copyModal, setCopyModal] = useState<{ open: boolean; sourceRow: any } | null>(null);
  const [copyTargets, setCopyTargets] = useState<Set<number>>(new Set());
  const [copySearch, setCopySearch] = useState('');

  // ── Bulk column apply modal ──
  const [bulkColumnModal, setBulkColumnModal] = useState<{ open: boolean; dateStr: string; dayName: string } | null>(null);
  const [bulkShiftId, setBulkShiftId] = useState<string>('');
  const [bulkIsDayOff, setBulkIsDayOff] = useState(false);
  const [shiftColorOverrides, setShiftColorOverrides] = useState<Record<number, string>>(() => {
    try {
      if (typeof window === 'undefined') return {};
      const raw = window.localStorage.getItem(SHIFT_COLOR_STORAGE_KEY);
      if (!raw) return {};

      const parsed = JSON.parse(raw);
      if (!parsed || typeof parsed !== 'object') return {};

      return Object.fromEntries(
        Object.entries(parsed)
          .map(([id, color]) => [Number(id), normalizeHexColor(String(color))])
          .filter(([id, color]) => Number.isFinite(id) && Boolean(color))
      ) as Record<number, string>;
    } catch {
      return {};
    }
  });
  const [displayColorOverrides, setDisplayColorOverrides] = useState<Partial<Record<DisplayColorKey, string>>>(() => {
    try {
      if (typeof window === 'undefined') return {};
      const raw = window.localStorage.getItem(DISPLAY_COLOR_STORAGE_KEY);
      if (!raw) return {};

      const parsed = JSON.parse(raw);
      if (!parsed || typeof parsed !== 'object') return {};

      const next: Partial<Record<DisplayColorKey, string>> = {};
      DISPLAY_COLOR_KEYS.forEach((key) => {
        const value = (parsed as Record<string, unknown>)[key];
        const normalized = normalizeHexColor(typeof value === 'string' ? value : null);
        if (normalized) {
          next[key] = normalized;
        }
      });

      return next;
    } catch {
      return {};
    }
  });

  // ── Fetch weekly plan ──
  const { data: weeklyData, isLoading } = useQuery({
    queryKey: [...QUERY_KEYS.operationsWeeklyPlan(), { week_start_date: weekStart }],
    queryFn: () => operationsAPI.weeklyPlan({ week_start_date: weekStart }),
    enabled: !!weekStart,
  });

  const payload = (weeklyData as any)?.data ?? {};
  const rows: any[] = Array.isArray(payload.rows) ? payload.rows : [];
  const dates: any[] = Array.isArray(payload.dates) ? payload.dates : [];
  const shifts: any[] = Array.isArray(payload.shifts) ? payload.shifts : (parentShifts ?? []);

  useEffect(() => {
    try {
      if (typeof window === 'undefined') return;
      window.localStorage.setItem(SHIFT_COLOR_STORAGE_KEY, JSON.stringify(shiftColorOverrides));
    } catch {
      // Ignore storage failures and keep the UI usable.
    }
  }, [shiftColorOverrides]);

  useEffect(() => {
    try {
      if (typeof window === 'undefined') return;
      window.localStorage.setItem(DISPLAY_COLOR_STORAGE_KEY, JSON.stringify(displayColorOverrides));
    } catch {
      // Ignore storage failures and keep the UI usable.
    }
  }, [displayColorOverrides]);

  const departmentScopedRows = useMemo(() => {
    if (!filterDepartmentId) return rows;
    return rows.filter((r: any) => String(r.department_id ?? '') === String(filterDepartmentId));
  }, [rows, filterDepartmentId]);

  // ── Filtered rows (search + shift filter) ──
  const filteredRows = useMemo(() => {
    let result = departmentScopedRows;
    if (search.trim()) {
      const q = search.toLowerCase().trim();
      result = result.filter((r: any) =>
        (r.employee_name ?? '').toLowerCase().includes(q) ||
        (r.employee_code ?? '').toLowerCase().includes(q) ||
        (r.department_name ?? '').toLowerCase().includes(q)
      );
    }
    if (filterShiftId) {
      const fid = Number(filterShiftId);
      result = result.filter((r: any) => {
        return dates.some((d: any) => {
          const dateStr = typeof d === 'string' ? d : d.date;
          const key = `${r.employee_id}|${dateStr}`;
          const change = localChanges[key];
          const cell = r.days?.[dateStr] ?? {};
          const sid = change !== undefined ? change.shift_id : (cell.shift_id ?? null);
          return sid != null && Number(sid) === fid;
        });
      });
    }
    return result;
  }, [departmentScopedRows, search, filterShiftId, dates, localChanges]);

  // ── Save mutation ──
  const saveMutation = useMutation({
    mutationFn: (entries: any[]) => operationsAPI.saveWeeklyPlan({ entries }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.operationsWeeklyPlan() });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.shiftAssignments() });
      setLocalChanges({});
      toast.success('Недельный график сохранён!');
    },
    onError: () => toast.error('Ошибка при сохранении'),
  });

  // ── Helpers ──
  const resetWeek = (newWeek: string) => { setWeekStart(newWeek); setLocalChanges({}); };

  const handleCellChange = (empId: number, date: string, shiftId: string, baseCell?: any) => {
    const key = `${empId}|${date}`;

    if (shiftId === '') {
      const baseShiftId = baseCell?.shift_id != null ? Number(baseCell.shift_id) : null;
      const baseIsDayOff = Boolean(baseCell?.is_day_off);
      if (!baseIsDayOff && baseShiftId === null) {
        setLocalChanges((prev) => { const next = { ...prev }; delete next[key]; return next; });
        return;
      }
      setLocalChanges((prev) => ({
        ...prev,
        [key]: { employee_id: empId, date, shift_id: null, is_day_off: false, reason: null },
      }));
      return;
    }

    const shiftIdNumber = Number(shiftId);
    const baseShiftId = baseCell?.shift_id != null ? Number(baseCell.shift_id) : null;
    const baseIsDayOff = Boolean(baseCell?.is_day_off);
    if (!baseIsDayOff && baseShiftId === shiftIdNumber) {
      setLocalChanges((prev) => { const next = { ...prev }; delete next[key]; return next; });
      return;
    }
    setLocalChanges((prev) => ({
      ...prev,
      [key]: { employee_id: empId, date, shift_id: shiftIdNumber, is_day_off: false, reason: null },
    }));
  };

  const handleDayOffToggle = (empId: number, date: string, checked: boolean, baseCell?: any) => {
    const key = `${empId}|${date}`;
    if (checked) {
      const defaultReason = baseCell?.day_off_reason ?? '';
      const reasonInput = window.prompt("Причина выходного:", defaultReason);
      if (reasonInput == null) return;
      const reason = reasonInput.trim();
      if (!reason) { toast.warning("Введите причину выходного"); return; }
      setLocalChanges((prev) => ({
        ...prev,
        [key]: { employee_id: empId, date, shift_id: null, is_day_off: true, reason },
      }));
      return;
    }
    const baseShiftId = baseCell?.shift_id != null ? Number(baseCell.shift_id) : null;
    if (baseShiftId != null) {
      setLocalChanges((prev) => ({
        ...prev,
        [key]: { employee_id: empId, date, shift_id: baseShiftId, is_day_off: false, reason: null },
      }));
    } else {
      setLocalChanges((prev) => { const next = { ...prev }; delete next[key]; return next; });
    }
  };

  const handleSave = () => {
    const entries = Object.values(localChanges);
    if (entries.length === 0) { toast.info("Нет изменений"); return; }
    saveMutation.mutate(entries);
  };

  // ── FEATURE: Copy Week → Next Week ──
  const handleCopyToNextWeek = () => {
    const nextMonday = new Date(weekStart + 'T00:00:00');
    nextMonday.setDate(nextMonday.getDate() + 7);
    const nextWeekDates = getWeekDates(toDateInput(nextMonday.toISOString()));

    const entries: any[] = [];
    for (const row of rows) {
      for (let i = 0; i < 7; i++) {
        const srcDate = typeof dates[i] === 'string' ? dates[i] : dates[i]?.date ?? '';
        const key = `${row.employee_id}|${srcDate}`;
        const change = localChanges[key];
        const cell = row.days?.[srcDate] ?? {};

        const isDayOff = change !== undefined ? Boolean(change.is_day_off) : Boolean(cell.is_day_off);
        const shiftId = change !== undefined ? change.shift_id : (cell.shift_id != null ? Number(cell.shift_id) : null);
        const reason = change !== undefined ? (change.reason ?? '') : (cell.day_off_reason ?? '');

        if (isDayOff) {
          entries.push({ employee_id: row.employee_id, date: nextWeekDates[i], shift_id: null, is_day_off: true, reason: reason || 'Выходной' });
        } else if (shiftId) {
          entries.push({ employee_id: row.employee_id, date: nextWeekDates[i], shift_id: shiftId, is_day_off: false });
        }
      }
    }

    if (entries.length === 0) { toast.info("Нет данных для копирования"); return; }
    if (!window.confirm(`Скопировать ${entries.length} назначений на следующую неделю?`)) return;

    saveMutation.mutate(entries, {
      onSuccess: () => {
        toast.success('Скопировано на следующую неделю!');
        resetWeek(toDateInput(nextMonday.toISOString()));
      },
    });
  };

  // ── FEATURE: Copy Employee Row → Other Employees ──
  const openCopyModal = (row: any) => {
    setCopyModal({ open: true, sourceRow: row });
    setCopyTargets(new Set());
    setCopySearch('');
  };

  const handleCopyEmployee = () => {
    if (!copyModal?.sourceRow || copyTargets.size === 0) return;
    const src = copyModal.sourceRow;
    const newChanges = { ...localChanges };

    for (const targetEmpId of copyTargets) {
      for (const d of dates) {
        const dateStr = typeof d === 'string' ? d : d.date;
        const srcKey = `${src.employee_id}|${dateStr}`;
        const tgtKey = `${targetEmpId}|${dateStr}`;
        const change = localChanges[srcKey];
        const cell = src.days?.[dateStr] ?? {};

        const isDayOff = change !== undefined ? Boolean(change.is_day_off) : Boolean(cell.is_day_off);
        const shiftId = change !== undefined ? change.shift_id : (cell.shift_id != null ? Number(cell.shift_id) : null);
        const reason = change !== undefined ? (change.reason ?? '') : (cell.day_off_reason ?? '');

        if (isDayOff) {
          newChanges[tgtKey] = { employee_id: targetEmpId, date: dateStr, shift_id: null, is_day_off: true, reason: reason || 'Выходной' };
        } else if (shiftId) {
          newChanges[tgtKey] = { employee_id: targetEmpId, date: dateStr, shift_id: shiftId, is_day_off: false, reason: null };
        } else {
          newChanges[tgtKey] = { employee_id: targetEmpId, date: dateStr, shift_id: null, is_day_off: false, reason: null };
        }
      }
    }

    setLocalChanges(newChanges);
    setCopyModal(null);
    toast.success(`${copyTargets.size} сотрудникам скопировано (нажмите «Сохранить»)`);
  };

  // ── FEATURE: Bulk Column Apply (all employees on one day) ──
  const handleBulkColumnApply = () => {
    if (!bulkColumnModal) return;
    const dateStr = bulkColumnModal.dateStr;
    const newChanges = { ...localChanges };

    for (const row of filteredRows) {
      const key = `${row.employee_id}|${dateStr}`;
      if (bulkIsDayOff) {
        newChanges[key] = { employee_id: row.employee_id, date: dateStr, shift_id: null, is_day_off: true, reason: 'Выходной' };
      } else if (bulkShiftId) {
        newChanges[key] = { employee_id: row.employee_id, date: dateStr, shift_id: Number(bulkShiftId), is_day_off: false, reason: null };
      } else {
        newChanges[key] = { employee_id: row.employee_id, date: dateStr, shift_id: null, is_day_off: false, reason: null };
      }
    }

    setLocalChanges(newChanges);
    setBulkColumnModal(null);
    setBulkShiftId('');
    setBulkIsDayOff(false);
    toast.success(`${filteredRows.length} сотрудникам назначена ${bulkIsDayOff ? 'выходной' : 'смена'} (нажмите «Сохранить»)`);
  };

  // ── Excuse handlers ──
  const openExcuse = (empId: number, empName: string, date: string) => {
    setExcuseModal({ open: true, empId, empName, date });
    setExcuseForm({ excused_until: '11:00', excuse_reason: '' });
  };

  const findTimesheetForEmployeeDate = async (empId: number, date: string) => {
    const tsData = await timesheetsAPI.list({
      employee_id: empId as any,
      date_from: date,
      date_to: date,
    });

    const tsList = (tsData as any)?.data?.data ?? (tsData as any)?.data ?? [];

    return Array.isArray(tsList) ? tsList[0] ?? null : null;
  };

  const handleSaveExcuse = async () => {
    if (!excuseModal) return;
    if (!excuseForm.excused_until || !excuseForm.excuse_reason) {
      toast.warning('Введите время и причину');
      return;
    }
    setExcuseLoading(true);
    try {
      const ts = await findTimesheetForEmployeeDate(excuseModal.empId, excuseModal.date);
      if (!ts) { toast.error('Табель за эту дату не найден'); setExcuseLoading(false); return; }
      await timesheetsAPI.setExcuse(ts.id, excuseForm);
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.operationsWeeklyPlan() });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.timesheets() });
      toast.success('Разрешение прихода сохранено!');
      setExcuseModal(null);
    } catch (err: any) {
      toast.error(err?.message || 'Произошла ошибка');
    } finally {
      setExcuseLoading(false);
    }
  };

  // ── Early leave excuse handlers ──
  const openEarlyLeaveExcuse = (empId: number, empName: string, date: string, cell?: any) => {
    const existingTime = typeof cell?.early_leave_excused_until === 'string' ? cell.early_leave_excused_until : '';
    const existingReason = typeof cell?.early_leave_excuse_reason === 'string' ? cell.early_leave_excuse_reason : '';

    setEarlyLeaveModal({
      open: true,
      empId,
      empName,
      date,
      hasExisting: Boolean(existingTime),
    });
    setEarlyLeaveForm({
      early_leave_excused_until: existingTime || '16:00',
      early_leave_excuse_reason: existingReason,
    });
  };

  const handleSaveEarlyLeaveExcuse = async () => {
    if (!earlyLeaveModal) return;
    if (!earlyLeaveForm.early_leave_excused_until || !earlyLeaveForm.early_leave_excuse_reason) {
      toast.warning('Введите время и причину');
      return;
    }
    setEarlyLeaveLoading(true);
    try {
      const ts = await findTimesheetForEmployeeDate(earlyLeaveModal.empId, earlyLeaveModal.date);
      if (!ts) { toast.error('Табель за эту дату не найден'); setEarlyLeaveLoading(false); return; }
      await timesheetsAPI.setEarlyLeaveExcuse(ts.id, earlyLeaveForm);
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.operationsWeeklyPlan() });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.timesheets() });
      toast.success('Разрешение раннего ухода сохранено!');
      setEarlyLeaveModal(null);
    } catch (err: any) {
      toast.error(err?.message || 'Произошла ошибка');
    } finally {
      setEarlyLeaveLoading(false);
    }
  };

  const handleRemoveEarlyLeaveExcuse = async () => {
    if (!earlyLeaveModal) return;

    setEarlyLeaveLoading(true);
    try {
      const ts = await findTimesheetForEmployeeDate(earlyLeaveModal.empId, earlyLeaveModal.date);
      if (!ts) {
        toast.error('Табель за эту дату не найден');
        return;
      }

      await timesheetsAPI.removeEarlyLeaveExcuse(ts.id);
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.operationsWeeklyPlan() });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.timesheets() });
      toast.success('Разрешение раннего ухода удалено!');
      setEarlyLeaveModal(null);
    } catch (err: any) {
      toast.error(err?.message || 'Произошла ошибка');
    } finally {
      setEarlyLeaveLoading(false);
    }
  };

  // ── Day off handlers ──
  const openDayOffModal = (empId: number, empName: string, date: string, reason = '') => {
    setDayOffModal({ empId, empName, date });
    setDayOffReason(reason);
  };

  const handleSaveDayOff = () => {
    if (!dayOffModal) return;
    const reason = dayOffReason.trim();
    if (!reason) {
      toast.warning('Введите причину выходного');
      return;
    }

    const key = `${dayOffModal.empId}|${dayOffModal.date}`;
    setLocalChanges((prev) => ({
      ...prev,
      [key]: {
        employee_id: dayOffModal.empId,
        date: dayOffModal.date,
        shift_id: null,
        is_day_off: true,
        reason,
      },
    }));
    setDayOffModal(null);
    toast.success('Выходной назначен');
  };

  // ── Cell modal handlers ──
  const handleCellClick = (e: React.MouseEvent<HTMLElement>, empId: number, empName: string, dateStr: string, cell: any) => {
    if (!canManage) return;
    e.stopPropagation();
    setActiveCell({ empId, empName, date: dateStr, cell });
  };

  const handlePopoverShiftSelect = (shiftIdVal: string) => {
    if (!activeCell) return;
    handleCellChange(activeCell.empId, activeCell.date, shiftIdVal, activeCell.cell);
    setActiveCell(null);
  };

  const handleApplyShiftThroughWeek = (shiftIdVal: string) => {
    if (!activeCell) return;

    const shiftIdNumber = Number(shiftIdVal);
    const row = rows.find((item: any) => item.employee_id === activeCell.empId);
    const startIndex = dates.findIndex((d: any) => (typeof d === 'string' ? d : d.date) === activeCell.date);

    if (!row || startIndex === -1) {
      handlePopoverShiftSelect(shiftIdVal);
      return;
    }

    setLocalChanges((prev) => {
      const next = { ...prev };

      for (let index = startIndex; index < dates.length; index += 1) {
        const dateStr = typeof dates[index] === 'string' ? dates[index] : dates[index].date;
        const key = `${activeCell.empId}|${dateStr}`;
        const baseCell = row.days?.[dateStr] ?? {};
        const baseShiftId = baseCell?.shift_id != null ? Number(baseCell.shift_id) : null;
        const baseIsDayOff = Boolean(baseCell?.is_day_off);

        if (!baseIsDayOff && baseShiftId === shiftIdNumber) {
          delete next[key];
        } else {
          next[key] = {
            employee_id: activeCell.empId,
            date: dateStr,
            shift_id: shiftIdNumber,
            is_day_off: false,
            reason: null,
          };
        }
      }

      return next;
    });

    setActiveCell(null);
    const affectedDays = dates.length - startIndex;
    toast.success(`Смена назначена на ${affectedDays} дн.`);
  };

  const handlePopoverDayOff = () => {
    if (!activeCell) return;
    const { empId, empName, date, cell } = activeCell;
    const key = `${empId}|${date}`;
    const change = localChanges[key];
    const isDO = change !== undefined ? Boolean(change.is_day_off) : Boolean(cell?.is_day_off);
    if (isDO) {
      const baseShiftId = cell?.shift_id != null ? Number(cell.shift_id) : null;
      if (baseShiftId != null) {
        setLocalChanges(prev => ({ ...prev, [key]: { employee_id: empId, date, shift_id: baseShiftId, is_day_off: false, reason: null } }));
      } else {
        setLocalChanges(prev => { const n = { ...prev }; delete n[key]; return n; });
      }
      setActiveCell(null);
    } else {
      setActiveCell(null);
      openDayOffModal(empId, empName, date, change?.reason ?? cell?.day_off_reason ?? '');
    }
  };

  // Quick day off for single day with default reason
  const handleDayOffSingleDay = (reason = 'выходной') => {
    if (!activeCell) return;
    const { empId, date } = activeCell;
    const key = `${empId}|${date}`;
    setLocalChanges((prev) => ({
      ...prev,
      [key]: {
        employee_id: empId,
        date,
        shift_id: null,
        is_day_off: true,
        reason,
      },
    }));
    setActiveCell(null);
    toast.success('Выходной назначен');
  };

  // Apply day off for remaining days of the week with default reason
  const handleApplyDayOffThroughWeek = (reason = 'выходной') => {
    if (!activeCell) return;

    const row = rows.find((item: any) => item.employee_id === activeCell.empId);
    const startIndex = dates.findIndex((d: any) => (typeof d === 'string' ? d : d.date) === activeCell.date);

    if (!row || startIndex === -1) {
      handleDayOffSingleDay(reason);
      return;
    }

    setLocalChanges((prev) => {
      const next = { ...prev };

      for (let index = startIndex; index < dates.length; index += 1) {
        const dateStr = typeof dates[index] === 'string' ? dates[index] : dates[index].date;
        const key = `${activeCell.empId}|${dateStr}`;
        next[key] = {
          employee_id: activeCell.empId,
          date: dateStr,
          shift_id: null,
          is_day_off: true,
          reason,
        };
      }

      return next;
    });

    setActiveCell(null);
    const affectedDays = dates.length - startIndex;
    toast.success(`Выходной назначен на ${affectedDays} дн.`);
  };

  const handlePopoverClear = () => {
    if (!activeCell) return;
    handleCellChange(activeCell.empId, activeCell.date, '', activeCell.cell);
    setActiveCell(null);
  };

  const handlePopoverExcuse = () => {
    if (!activeCell) return;
    const { empId, empName, date } = activeCell;
    setActiveCell(null);
    openExcuse(empId, empName, date);
  };

  const handlePopoverEarlyLeave = () => {
    if (!activeCell) return;
    const { empId, empName, date, cell } = activeCell;
    setActiveCell(null);
    openEarlyLeaveExcuse(empId, empName, date, cell);
  };

  // ── Shift colors ──
  const shiftPalettes: Record<number, ShiftPalette> = useMemo(() => {
    const map: Record<number, ShiftPalette> = {};
    shifts.forEach((shift: any, index: number) => {
      const fallback = DEFAULT_SHIFT_COLORS[index % DEFAULT_SHIFT_COLORS.length];
      const hex = shiftColorOverrides[shift.id] ?? fallback;
      map[shift.id] = buildShiftPalette(hex);
    });
    return map;
  }, [shifts, shiftColorOverrides]);

  const getShiftPalette = (shiftId?: number | string | null): ShiftPalette | null => {
    if (shiftId == null || shiftId === '') return null;
    const numericId = Number(shiftId);
    return Number.isFinite(numericId) ? (shiftPalettes[numericId] ?? null) : null;
  };

  const updateShiftColor = (shiftId: number, value: string) => {
    const normalized = normalizeHexColor(value);
    if (!normalized) return;
    setShiftColorOverrides((prev) => ({ ...prev, [shiftId]: normalized }));
  };

  const displayPalettes: Record<DisplayColorKey, ShiftPalette> = useMemo(() => {
    return DISPLAY_COLOR_KEYS.reduce((acc, key) => {
      acc[key] = buildShiftPalette(displayColorOverrides[key] ?? DEFAULT_DISPLAY_COLORS[key]);
      return acc;
    }, {} as Record<DisplayColorKey, ShiftPalette>);
  }, [displayColorOverrides]);

  const getDisplayPalette = (key: DisplayColorKey): ShiftPalette => displayPalettes[key];

  const updateDisplayColor = (key: DisplayColorKey, value: string) => {
    const normalized = normalizeHexColor(value);
    if (!normalized) return;
    setDisplayColorOverrides((prev) => ({ ...prev, [key]: normalized }));
  };

  const dayOffPalette = getDisplayPalette('day_off');
  const emptyPalette = getDisplayPalette('empty');

  const resetCalendarColors = () => {
    setShiftColorOverrides({});
    setDisplayColorOverrides({});
    toast.success('Цвета отображения сброшены');
  };

  const todayStr = toDateInput(new Date().toISOString());
  const calendarStatusConfig: Record<string, CalendarStatusTone> = {
    working: { label: 'В работе', className: 'border-emerald-200 bg-emerald-50 text-emerald-700' },
    present: { label: 'Пришёл', className: 'border-emerald-200 bg-emerald-50 text-emerald-700' },
    late: { label: 'Опоздал', className: 'border-amber-200 bg-amber-50 text-amber-700' },
    early_leave: { label: 'Ушёл рано', className: 'border-amber-200 bg-amber-50 text-amber-700' },
    no_checkout: { label: 'Нет выхода', className: 'border-violet-200 bg-violet-50 text-violet-700' },
    no_checkin: { label: 'Нет входа', className: 'border-violet-200 bg-violet-50 text-violet-700' },
    absent: { label: 'Отсутствие', className: 'border-rose-200 bg-rose-50 text-rose-700' },
    no_show: { label: 'Не явился', className: 'border-rose-200 bg-rose-50 text-rose-700' },
    pending: { label: 'Ожидается', className: 'border-slate-200 bg-slate-100 text-slate-600' },
    weekend: { label: 'Выходной', className: 'border-slate-200 bg-slate-100 text-slate-600' },
    holiday: { label: 'Праздник', className: 'border-slate-200 bg-slate-100 text-slate-600' },
    day_off: { label: 'Выходной', style: buildBadgeStyle(dayOffPalette) },
    leave: { label: 'Отпуск', className: 'border-sky-200 bg-sky-50 text-sky-700' },
    unpaid_leave: { label: 'Без оплаты', className: 'border-slate-200 bg-slate-100 text-slate-600' },
  };

  const getCalendarStatus = (cell: any, dateStr: string) => {
    const rawStatus = cell?.timesheet_status;
    if (!rawStatus) return null;

    if (dateStr === todayStr && cell?.first_in_at && !cell?.last_out_at && (rawStatus === 'present' || rawStatus === 'late')) {
      return 'working';
    }

    return rawStatus;
  };

  const buildCellBadges = (cell: any, statusMeta: CalendarStatusTone | null): CalendarBadge[] => {
    const badges: CalendarBadge[] = [];

    if (statusMeta) {
      badges.push({ key: 'status', label: statusMeta.label, className: statusMeta.className, style: statusMeta.style });
    }
    if (cell.first_in_at || cell.last_out_at) {
      const attendanceLabel = cell.first_in_at && cell.last_out_at
        ? `${cell.first_in_at}-${cell.last_out_at}`
        : cell.first_in_at
          ? `Вх ${cell.first_in_at}`
          : `Вых ${cell.last_out_at}`;

      badges.push({ key: 'attendance_window', label: attendanceLabel, className: 'border-slate-200 bg-white/85 text-slate-600' });
    }
    if (cell.excused_until) {
      badges.push({
        key: 'excused_until',
        label: `R ${cell.excused_until}`,
        className: 'border-blue-200 bg-blue-50 text-blue-700',
        title: cell.excuse_reason || undefined,
      });
    }
    if (cell.early_leave_excused_until) {
      badges.push({
        key: 'early_leave_excused_until',
        label: `E ${cell.early_leave_excused_until}`,
        className: 'border-amber-200 bg-amber-50 text-amber-700',
        title: cell.early_leave_excuse_reason || undefined,
      });
    }
    if (cell.late_minutes) {
      badges.push({ key: 'late_minutes', label: `+${cell.late_minutes}m`, className: 'border-rose-200 bg-rose-50 text-rose-700' });
    }
    if (cell.early_leave_minutes) {
      badges.push({ key: 'early_leave_minutes', label: `-${cell.early_leave_minutes}m`, className: 'border-orange-200 bg-orange-50 text-orange-700' });
    }
    if (cell.notes) {
      badges.push({ key: 'notes', label: 'Прим.', className: 'border-slate-200 bg-slate-100 text-slate-600', title: cell.notes });
    }

    return badges;
  };

  const renderCellBadges = (badges: CalendarBadge[], maxVisible = 2) => {
    if (badges.length === 0) return null;

    const visibleBadges = badges.slice(0, maxVisible);
    const hiddenCount = badges.length - visibleBadges.length;

    return (
      <div className="mt-1 flex max-h-[1.8rem] flex-wrap justify-center gap-0.5 overflow-hidden">
        {visibleBadges.map((badge) => (
          <span
            key={badge.key}
            title={badge.title}
            className={`rounded-full border px-1 py-0.5 text-[8px] font-semibold leading-none ${badge.className ?? ''}`}
            style={badge.style}
          >
            {badge.label}
          </span>
        ))}
        {hiddenCount > 0 && (
          <span className="rounded-full border border-slate-200 bg-slate-100 px-1 py-0.5 text-[8px] font-semibold leading-none text-slate-500">
            +{hiddenCount}
          </span>
        )}
      </div>
    );
  };

  const changesCount = Object.keys(localChanges).length;

  // ── Pagination ──
  const PAGE_SIZE = 10;
  const [page, setPage] = useState(1);
  const totalPages = Math.max(1, Math.ceil(filteredRows.length / PAGE_SIZE));
  const paginatedRows = useMemo(() => filteredRows.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE), [filteredRows, page]);

  useEffect(() => { setPage(1); }, [weekStart, search, filterDepartmentId, filterShiftId]);

  // ── Stats ──
  const stats = useMemo(() => {
    const assigned = departmentScopedRows.filter((r: any) =>
      dates.some((d: any) => {
        const ds = typeof d === 'string' ? d : d.date;
        const key = `${r.employee_id}|${ds}`;
        const change = localChanges[key];
        const cell = r.days?.[ds] ?? {};
        return change !== undefined ? (change.shift_id || change.is_day_off) : (cell.shift_id || cell.is_day_off);
      })
    ).length;
    return { total: departmentScopedRows.length, assigned, unassigned: departmentScopedRows.length - assigned };
  }, [departmentScopedRows, dates, localChanges]);

  const activeCellDraft = useMemo(() => {
    if (!activeCell) return null;

    const key = `${activeCell.empId}|${activeCell.date}`;
    const change = localChanges[key];
    const sourceCell = activeCell.cell ?? {};
    const isDayOff = change !== undefined ? Boolean(change.is_day_off) : Boolean(sourceCell.is_day_off);
    const currentShiftId = isDayOff
      ? null
      : change !== undefined
        ? (change.shift_id != null ? Number(change.shift_id) : null)
        : (sourceCell.shift_id != null ? Number(sourceCell.shift_id) : null);
    const selectedShift = currentShiftId != null
      ? shifts.find((s: any) => s.id === currentShiftId) ?? null
      : null;
    const dayOffReasonValue = isDayOff
      ? (change !== undefined ? (change.reason ?? '') : (sourceCell.day_off_reason ?? ''))
      : '';
    const dateObj = new Date(activeCell.date + 'T00:00:00');
    const startIndex = dates.findIndex((d: any) => (typeof d === 'string' ? d : d.date) === activeCell.date);
    const remainingDaysCount = startIndex === -1 ? 1 : dates.length - startIndex;

    return {
      isDayOff,
      currentShiftId,
      selectedShift,
      dayOffReason: dayOffReasonValue,
      dayLabel: DAY_NAMES_FULL[dateObj.getDay()],
      dateLabel: fmtDate(activeCell.date) ?? activeCell.date,
      remainingDaysCount,
      canApplyThroughWeek: remainingDaysCount > 1,
    };
  }, [activeCell, localChanges, shifts, dates]);

  // ── Helpers ──
  const toggleSet = (set: Set<number>, id: number): Set<number> => {
    const next = new Set(set);
    if (next.has(id)) {
      next.delete(id);
    } else {
      next.add(id);
    }
    return next;
  };

  return (
    <div className="space-y-3">
      {/* ══════ TOP BAR: Navigation + Stats + Quick Actions ══════ */}
      <Card>
        <CardContent className="py-3">
          {/* Row 0: PROMINENT WEEK HEADER */}
          {(() => {
            const wInfo = formatWeekRange(weekStart);
            const isCurrentWeek = weekStart === getWeekMonday(0);
            return (
              <div className="flex flex-wrap items-center gap-2 pb-3 mb-3 border-b border-slate-100">
                <Calendar className="w-5 h-5 text-blue-600" />
                <div className="flex items-baseline gap-2">
                  <span className="text-base font-bold tracking-tight text-slate-900 sm:text-lg">{wInfo.range}</span>
                  <span className="text-xs font-medium text-slate-500">· Неделя {wInfo.weekNum}</span>
                </div>
                {isCurrentWeek && (
                  <span className="inline-flex items-center rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-semibold text-emerald-700">
                    Текущая
                  </span>
                )}
              </div>
            );
          })()}

          {/* Row 1: Navigation + Stats */}
          <div className="flex flex-col gap-3 lg:flex-row lg:items-center">
            {/* Week nav */}
            <div className="flex items-center gap-1.5">
              <Button variant="outline" size="sm" className="h-9 w-9 p-0" onClick={() => resetWeek(getWeekMonday(-1 + Math.round((new Date(weekStart).getTime() - new Date(getWeekMonday()).getTime()) / 604800000)))}>
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <Button variant="outline" size="sm" className="h-9 px-4 font-medium" onClick={() => resetWeek(getWeekMonday(0))}>
                Текущая неделя
              </Button>
              <Button variant="outline" size="sm" className="h-9 w-9 p-0" onClick={() => resetWeek(getWeekMonday(1 + Math.round((new Date(weekStart).getTime() - new Date(getWeekMonday()).getTime()) / 604800000)))}>
                <ChevronRight className="w-4 h-4" />
              </Button>
              <Input type="date" value={weekStart} onChange={(e) => resetWeek(e.target.value)} className="max-w-[150px] h-9 text-sm ml-1" />
            </div>

            {/* Stats badges */}
            <div className="flex items-center gap-2 lg:ml-auto">
              <div className="flex items-center gap-1 text-xs text-slate-500 bg-slate-50 rounded-full px-3 py-1.5 border">
                <Users className="w-3.5 h-3.5" />
                <span className="font-semibold text-slate-700">{stats.total}</span> сотр.
              </div>
              <div className="flex items-center gap-1 text-xs text-emerald-600 bg-emerald-50 rounded-full px-3 py-1.5 border border-emerald-200">
                <Check className="w-3.5 h-3.5" />
                <span className="font-semibold">{stats.assigned}</span> назначено
              </div>
              {stats.unassigned > 0 && (
                <div className="flex items-center gap-1 text-xs text-amber-600 bg-amber-50 rounded-full px-3 py-1.5 border border-amber-200">
                  <span className="font-semibold">{stats.unassigned}</span> свободно
                </div>
              )}
            </div>
          </div>

          {/* Row 2: Search + Filter + Shift Colors */}
          <div className="mt-3 flex flex-col gap-2 border-t border-slate-100 pt-3">
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
            {/* Search */}
            <div className="relative flex-1 max-w-xs">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="text"
                placeholder="Поиск сотрудника (имя, код, отдел)..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full h-9 pl-9 pr-8 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-400 bg-white"
              />
              {search && (
                <button className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-300 hover:text-slate-500" onClick={() => setSearch('')}>
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>

            {/* Department filter */}
            <div className="flex items-center gap-1.5">
              <Filter className="w-3.5 h-3.5 text-slate-400" />
              <select
                className="h-9 rounded-lg border border-slate-200 text-xs px-2 focus:outline-none focus:ring-2 focus:ring-blue-400 bg-white min-w-[140px]"
                value={filterDepartmentId}
                onChange={(e) => setFilterDepartmentId(e.target.value)}
              >
                <option value="">Все разделы</option>
                {departments.map((dept: any) => (
                  <option key={dept.id} value={dept.id}>{dept.name}</option>
                ))}
              </select>
            </div>

            {/* Shift filter */}
            <div className="flex items-center gap-1.5">
              <Filter className="w-3.5 h-3.5 text-slate-400" />
              <select
                className="h-9 rounded-lg border border-slate-200 text-xs px-2 focus:outline-none focus:ring-2 focus:ring-blue-400 bg-white min-w-[120px]"
                value={filterShiftId}
                onChange={(e) => setFilterShiftId(e.target.value)}
              >
                <option value="">Все смены</option>
                {shifts.map((s: any) => (
                  <option key={s.id} value={s.id}>{s.name}</option>
                ))}
              </select>
            </div>
            </div>

            {(shifts.length > 0 || DISPLAY_COLOR_KEYS.length > 0) && (
              <div className="rounded-2xl border border-slate-200 bg-slate-50/60 px-3 py-3">
                <div className="mb-2 flex flex-wrap items-center gap-2">
                  <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">
                    <Palette className="h-3.5 w-3.5" />
                    Цвета отображения
                  </div>
                  <span className="text-[10px] text-slate-400">Сохраняется только в этом браузере. Другим пользователям не передается.</span>
                  <button
                    type="button"
                    onClick={resetCalendarColors}
                    className="ml-auto inline-flex items-center gap-1 rounded-full border border-slate-200 bg-white px-2.5 py-1 text-[10px] font-semibold text-slate-500 transition hover:border-slate-300 hover:text-slate-700"
                  >
                    <RotateCcw className="h-3 w-3" />
                    Сбросить
                  </button>
                </div>

                <div className="space-y-2">
                  {shifts.length > 0 && (
                    <div>
                      <div className="mb-1 text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-400">Смены</div>
                      <div className="flex flex-wrap gap-2">
                        {shifts.map((shift: any) => {
                          const palette = getShiftPalette(shift.id) ?? buildShiftPalette(DEFAULT_SHIFT_COLORS[0]);

                          return (
                            <div
                              key={shift.id}
                              className="flex min-w-0 items-center gap-2 rounded-xl border px-2.5 py-1.5 shadow-sm"
                              style={{
                                backgroundColor: palette.softBackground,
                                borderColor: palette.border,
                                color: palette.text,
                              }}
                            >
                              <span className="h-2.5 w-2.5 rounded-full shrink-0" style={{ backgroundColor: palette.accent }} />
                              <div className="min-w-0">
                                <div className="max-w-[8.5rem] truncate text-[11px] font-semibold">{shift.name}</div>
                                <div className="text-[10px]" style={{ color: palette.mutedText }}>
                                  {shift.start_time?.slice(0, 5)}–{shift.end_time?.slice(0, 5)}
                                </div>
                              </div>
                              <input
                                type="color"
                                aria-label={`Цвет смены ${shift.name}`}
                                value={palette.accent}
                                onChange={(e) => updateShiftColor(shift.id, e.target.value)}
                                className="h-6 w-6 shrink-0 cursor-pointer rounded-md border border-white/70 bg-transparent p-0"
                                title={`Изменить цвет смены ${shift.name}`}
                              />
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  <div>
                    <div className="mb-1 text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-400">Дополнительно</div>
                    <div className="flex flex-wrap gap-2">
                      {DISPLAY_COLOR_KEYS.map((colorKey) => {
                        const palette = getDisplayPalette(colorKey);
                        const meta = DISPLAY_COLOR_META[colorKey];

                        return (
                          <div
                            key={colorKey}
                            className="flex min-w-[11rem] items-center gap-2 rounded-xl border px-2.5 py-1.5 shadow-sm"
                            style={{
                              backgroundColor: palette.softBackground,
                              borderColor: palette.border,
                              color: palette.text,
                            }}
                          >
                            <span className="h-2.5 w-2.5 rounded-full shrink-0" style={{ backgroundColor: palette.accent }} />
                            <div className="min-w-0 flex-1">
                              <div className="truncate text-[11px] font-semibold">{meta.label}</div>
                              <div className="text-[10px]" style={{ color: palette.mutedText }}>
                                {meta.hint}
                              </div>
                            </div>
                            <input
                              type="color"
                              aria-label={`Цвет блока ${meta.label}`}
                              value={palette.accent}
                              onChange={(e) => updateDisplayColor(colorKey, e.target.value)}
                              className="h-6 w-6 shrink-0 cursor-pointer rounded-md border border-white/70 bg-transparent p-0"
                              title={`Изменить цвет блока ${meta.label}`}
                            />
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Row 3: Quick actions + Save bar */}
          {canManage && (SHOW_COPY_NEXT_WEEK || changesCount > 0) && (
            <div className="flex flex-wrap items-center gap-2 mt-3 pt-3 border-t border-slate-100">
              {/* Временно скрыто: копия на след. неделю */}
              {SHOW_COPY_NEXT_WEEK && (
                <Button variant="outline" size="sm" className="h-8 text-xs gap-1.5" onClick={handleCopyToNextWeek} disabled={saveMutation.isPending}>
                  <CopyPlus className="w-3.5 h-3.5" />
                  Копия на след. неделю
                </Button>
              )}

              {/* Save section */}
              {changesCount > 0 && (
                <>
                  {SHOW_COPY_NEXT_WEEK && <div className="w-px h-6 bg-slate-200 mx-1" />}
                  <div className="flex items-center gap-1.5 bg-amber-50 text-amber-700 px-3 py-1.5 rounded-full text-xs font-medium border border-amber-200">
                    <div className="w-2 h-2 rounded-full bg-amber-500 animate-pulse" />
                    {changesCount} измен.
                  </div>
                  <Button size="sm" className="rounded-full px-4 h-8" onClick={handleSave} disabled={saveMutation.isPending}>
                    <Save className="w-3.5 h-3.5 mr-1" />
                    {saveMutation.isPending ? 'Сохранение…' : 'Сохранить'}
                  </Button>
                  <button className="text-xs text-slate-400 hover:text-red-500 transition" onClick={() => setLocalChanges({})}>
                    Отмена
                  </button>
                </>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* ══════ CALENDAR GRID ══════ */}
      <Card className="overflow-hidden">
        <CardContent className="p-0">
          {isLoading ? (
            <div className="flex items-center justify-center py-16"><LoadingSpinner /></div>
          ) : rows.length === 0 ? (
            <div className="flex flex-col items-center py-20 text-slate-400 gap-3">
              <Calendar className="h-12 w-12 opacity-20" />
              <p className="text-sm font-medium">Сотрудники не найдены</p>
            </div>
          ) : filteredRows.length === 0 ? (
            <div className="flex flex-col items-center py-16 text-slate-400 gap-2">
              <Search className="h-10 w-10 opacity-20" />
              <p className="text-sm font-medium">По запросу «{search}» ничего не найдено</p>
              <button className="text-xs text-blue-500 hover:underline" onClick={() => { setSearch(''); setFilterDepartmentId(''); setFilterShiftId(''); }}>
                Сбросить фильтры
              </button>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto pb-2">
                <table className="w-full border-collapse" style={{ minWidth: `${Math.max(720, 188 + dates.length * 110)}px` }}>
                  <thead>
                    <tr className="bg-gradient-to-b from-slate-50 to-slate-100/80">
                      <th className="sticky left-0 z-20 min-w-[172px] bg-gradient-to-b from-slate-50 to-slate-100/80 px-2.5 py-2 text-left text-xs font-semibold text-slate-600 border-b-2 border-slate-200 sm:min-w-[188px] lg:min-w-[210px]">
                        <div className="flex items-center gap-2">
                          <Users className="w-3.5 h-3.5 text-slate-400" />
                          Сотрудник
                          {filteredRows.length < departmentScopedRows.length && (
                            <span className="text-[10px] text-blue-500 font-normal">({filteredRows.length}/{departmentScopedRows.length})</span>
                          )}
                        </div>
                      </th>
                      {dates.map((d: any) => {
                        const dateStr = typeof d === 'string' ? d : d.date;
                        const dayName = typeof d === 'object' ? (d.day_name ?? '') : '';
                        const dd = new Date(dateStr + 'T00:00:00');
                        const dayIdx = dd.getDay();
                        const isToday = dateStr === todayStr;
                        const isWeekend = dayIdx === 0 || dayIdx === 6;
                        return (
                          <th key={dateStr} className={`min-w-[106px] border-b-2 px-1 py-1.5 text-center sm:min-w-[114px] lg:min-w-[122px] ${
                            isToday ? 'border-blue-400 bg-blue-50/60' : isWeekend ? 'border-slate-200 bg-slate-100/40' : 'border-slate-200'
                          }`}>
                            <div className={`text-[11px] font-bold uppercase tracking-wider ${isToday ? 'text-blue-600' : isWeekend ? 'text-slate-400' : 'text-slate-500'}`}>
                              {dayName || DAY_NAMES_FULL[dayIdx]}
                            </div>
                            <div className={`text-[10px] font-normal ${isToday ? 'text-blue-500' : 'text-slate-400'}`}>
                              {dateStr.slice(5)}
                            </div>
                            {isToday && <div className="w-1.5 h-1.5 rounded-full bg-blue-500 mx-auto mt-0.5" />}
                            {/* Column bulk action */}
                            {canManage && (
                              <button
                                className="mt-1 text-[9px] text-slate-300 hover:text-blue-500 transition font-medium"
                                title={`Назначить смену на ${dayName || DAY_NAMES_FULL[dayIdx]} всем сотрудникам`}
                                onClick={() => { setBulkColumnModal({ open: true, dateStr, dayName: dayName || DAY_NAMES_FULL[dayIdx] }); setBulkShiftId(''); setBulkIsDayOff(false); }}
                              >
                                ▼ всем
                              </button>
                            )}
                          </th>
                        );
                      })}
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedRows.map((row: any, rowIdx: number) => {
                      // Count how many days this employee has a shift assigned
                      const assignedDays = dates.filter((d: any) => {
                        const ds = typeof d === 'string' ? d : d.date;
                        const key = `${row.employee_id}|${ds}`;
                        const change = localChanges[key];
                        const cell = row.days?.[ds] ?? {};
                        return change !== undefined ? (change.shift_id || change.is_day_off) : (cell.shift_id || cell.is_day_off);
                      }).length;

                      return (
                        <tr key={row.employee_id} className={`group transition-colors ${rowIdx % 2 === 0 ? 'bg-white' : 'bg-slate-50/30'} hover:bg-blue-50/20`}>
                          <td className={`sticky left-0 z-10 border-r border-slate-100 px-2 py-1.5 ${rowIdx % 2 === 0 ? 'bg-white' : 'bg-slate-50/30'} transition-colors group-hover:bg-blue-50/20 sm:px-2.5`}>
                            <div className="flex items-center gap-2">
                              <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-slate-200 to-slate-300 text-[11px] font-bold text-slate-600">
                                {(row.employee_name ?? '?')[0]}
                              </div>
                              <div className="min-w-0 flex-1">
                                <div className="font-semibold text-[13px] text-slate-800 truncate leading-tight">{row.employee_name ?? '–'}</div>
                                <div className="flex items-center gap-1.5">
                                  {row.employee_code && <span className="text-[10px] text-slate-400 font-mono">#{row.employee_code}</span>}
                                  {row.department_name && (
                                    <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-medium text-slate-500">
                                      {row.department_name}
                                    </span>
                                  )}
                                  {/* Mini progress bar */}
                                  <div className="flex gap-px" title={`${assignedDays}/7 дней назначено`}>
                                    {Array.from({ length: 7 }, (_, i) => {
                                      const ds = typeof dates[i] === 'string' ? dates[i] : dates[i]?.date ?? '';
                                      const key = `${row.employee_id}|${ds}`;
                                      const change = localChanges[key];
                                      const cell = row.days?.[ds] ?? {};
                                      const hasSomething = change !== undefined ? (change.shift_id || change.is_day_off) : (cell.shift_id || cell.is_day_off);
                                      return <div key={i} className={`w-1.5 h-1.5 rounded-sm ${hasSomething ? 'bg-emerald-400' : 'bg-slate-200'}`} />;
                                    })}
                                  </div>
                                </div>
                              </div>
                              {/* Copy row button */}
                              {canManage && (
                                <button
                                  className="opacity-0 group-hover:opacity-60 hover:!opacity-100 transition p-1 rounded hover:bg-blue-100 shrink-0"
                                  title="Скопировать этот график другим сотрудникам"
                                  onClick={() => openCopyModal(row)}
                                >
                                  <Copy className="w-3.5 h-3.5 text-blue-600" />
                                </button>
                              )}
                            </div>
                          </td>
                          {dates.map((d: any) => {
                            const dateStr = typeof d === 'string' ? d : d.date;
                            const key = `${row.employee_id}|${dateStr}`;
                            const cell = row.days?.[dateStr] ?? {};
                            const change = localChanges[key];
                            const isDirty = change !== undefined;
                            const isDayOff = isDirty ? Boolean(change?.is_day_off) : Boolean(cell.is_day_off);
                            const dayOffReason = isDirty ? (change?.reason ?? '') : (cell.day_off_reason ?? '');
                            const currentShiftId = isDayOff
                              ? ''
                              : isDirty
                                ? (change.shift_id === null ? '' : String(change.shift_id))
                                : (cell.shift_id != null ? String(cell.shift_id) : '');
                            const selectedShift = currentShiftId
                              ? shifts.find((s: any) => s.id === Number(currentShiftId))
                              : null;
                            const displayShiftName = currentShiftId
                              ? (selectedShift?.name || '—')
                              : (cell.shift_name || '');
                            const displayShiftTime = currentShiftId
                              ? (selectedShift ? `${selectedShift.start_time?.slice(0, 5)}–${selectedShift.end_time?.slice(0, 5)}` : '')
                              : (cell.shift_time ? String(cell.shift_time).replace(' - ', '–') : '');
                            const effectiveStatus = getCalendarStatus(cell, dateStr);
                            const statusMeta = effectiveStatus ? calendarStatusConfig[effectiveStatus] : null;
                            const hasExtraMeta = Boolean(
                              statusMeta ||
                              cell.first_in_at ||
                              cell.last_out_at ||
                              cell.excused_until ||
                              cell.early_leave_excused_until ||
                              cell.notes ||
                              cell.late_minutes ||
                              cell.early_leave_minutes ||
                              dayOffReason
                            );
                            const cellTooltip = [
                              isDayOff
                                ? `Выходной${dayOffReason ? `: ${dayOffReason}` : ''}`
                                : displayShiftName
                                  ? `Смена: ${displayShiftName}${displayShiftTime ? ` (${displayShiftTime})` : ''}`
                                  : 'Пустая ячейка',
                              statusMeta ? `Статус: ${statusMeta.label}` : null,
                              cell.first_in_at ? `Приход: ${cell.first_in_at}` : null,
                              cell.last_out_at ? `Уход: ${cell.last_out_at}` : null,
                              cell.excused_until ? `Разрешение прихода: ${cell.excused_until}${cell.excuse_reason ? ` (${cell.excuse_reason})` : ''}` : null,
                              cell.early_leave_excused_until ? `Разрешение раннего ухода: ${cell.early_leave_excused_until}${cell.early_leave_excuse_reason ? ` (${cell.early_leave_excuse_reason})` : ''}` : null,
                              cell.late_minutes ? `Опоздание: ${cell.late_minutes} мин` : null,
                              cell.early_leave_minutes ? `Ранний уход: ${cell.early_leave_minutes} мин` : null,
                              cell.notes ? `Комментарий: ${cell.notes}` : null,
                            ].filter(Boolean).join('\n');

                            const dd = new Date(dateStr + 'T00:00:00');
                            const dayIdx = dd.getDay();
                            const isToday = dateStr === todayStr;
                            const isWeekend = dayIdx === 0 || dayIdx === 6;

                            return (
                              <td key={dateStr} className={`border-r border-slate-100/50 px-1 py-1.5 text-center last:border-r-0 sm:px-1.5 ${
                                isDirty ? 'bg-blue-50/60' : isToday ? 'bg-blue-50/30' : isWeekend ? 'bg-slate-50/30' : ''
                              }`}>
                                {(() => {
                                  const shiftPalette = currentShiftId ? getShiftPalette(currentShiftId) : null;
                                  const cellPalette = isDayOff ? dayOffPalette : (shiftPalette ?? (!displayShiftName ? emptyPalette : null));
                                  const detailBadges = buildCellBadges(cell, statusMeta);
                                  const dayOffReasonPreview = dayOffReason.length > 18 ? `${dayOffReason.slice(0, 18).trim()}…` : dayOffReason;

                                  return (
                                <button
                                  type="button"
                                  onClick={(e) => canManage && handleCellClick(e, row.employee_id, row.employee_name, dateStr, cell)}
                                  disabled={!canManage}
                                  title={cellTooltip || undefined}
                                  className={`flex h-[88px] w-full items-center justify-center overflow-hidden rounded-xl border text-center transition-all sm:h-[94px] ${
                                    isDayOff
                                      ? 'shadow-sm'
                                      : currentShiftId
                                        ? 'shadow-sm'
                                        : isDirty
                                          ? 'shadow-sm'
                                          : 'border-dashed border-slate-200 bg-white/70 hover:border-slate-300 hover:bg-slate-50/70'
                                  } ${canManage ? 'cursor-pointer hover:-translate-y-[1px] hover:shadow-md active:translate-y-0' : ''}`}
                                  style={cellPalette ? {
                                    backgroundColor: cellPalette.background,
                                    borderColor: cellPalette.border,
                                    boxShadow: isDirty ? `0 0 0 1px ${rgba('#2563eb', 0.18)}` : undefined,
                                  } : (isDirty ? { boxShadow: `0 0 0 1px ${rgba('#2563eb', 0.18)}` } : undefined)}
                                >
                                  {isDayOff ? (
                                    <div className="flex h-full w-full flex-col justify-center overflow-hidden px-2 py-1.5 leading-tight">
                                      <div className="flex items-center justify-center gap-1">
                                        <span className="text-[12px] leading-none">☀</span>
                                        <span className="text-[11px] font-semibold" style={{ color: dayOffPalette.mutedText }}>Вых</span>
                                      </div>
                                      {dayOffReason && (
                                        <div className="mt-0.5 truncate text-[9px]" style={{ color: dayOffPalette.mutedText }} title={dayOffReason}>
                                          {dayOffReasonPreview}
                                        </div>
                                      )}
                                      {renderCellBadges(detailBadges, 1)}
                                    </div>
                                  ) : displayShiftName ? (
                                    <div className="flex h-full w-full flex-col justify-center overflow-hidden px-2 py-1.5">
                                      <div className="flex items-center justify-center gap-1">
                                        {currentShiftId && <span className="h-1.5 w-1.5 shrink-0 rounded-full" style={{ backgroundColor: shiftPalette?.accent ?? '#94a3b8' }} />}
                                        <div className="text-center leading-tight min-w-0">
                                          <div className={`truncate text-[11px] font-semibold ${currentShiftId ? '' : 'text-slate-600 opacity-70'}`}>
                                            {displayShiftName}
                                          </div>
                                          {displayShiftTime && (
                                            <div className="mt-0.5 font-mono text-[10px] leading-none" style={{ color: shiftPalette?.mutedText ?? '#64748b' }}>
                                              {displayShiftTime}
                                            </div>
                                          )}
                                        </div>
                                      </div>
                                      {renderCellBadges(detailBadges, 2)}
                                    </div>
                                  ) : (
                                    <div className="flex h-full w-full items-center justify-center overflow-hidden px-1.5 py-1">
                                      {hasExtraMeta ? (
                                        renderCellBadges(detailBadges, 2)
                                      ) : (
                                        <Plus className="h-3 w-3" style={{ color: canManage ? emptyPalette.mutedText : emptyPalette.border }} />
                                      )}
                                    </div>
                                  )}
                                </button>
                                  );
                                })()}
                              </td>
                            );
                          })}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between px-4 py-2.5 border-t border-slate-100 bg-slate-50/50">
                  <p className="text-xs text-slate-500">
                    <span className="font-medium text-slate-700">{(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, filteredRows.length)}</span>
                    {' / '}
                    <span className="font-medium text-slate-700">{filteredRows.length}</span> сотр.
                    {filteredRows.length < rows.length && <span className="text-slate-400"> (из {rows.length})</span>}
                  </p>
                  <div className="flex items-center gap-1">
                    <Button variant="outline" size="sm" className="h-7 w-7 p-0" disabled={page <= 1} onClick={() => setPage(page - 1)}>
                      <ChevronLeft className="w-3.5 h-3.5" />
                    </Button>
                    {Array.from({ length: totalPages }, (_, i) => i + 1)
                      .filter(p => p === 1 || p === totalPages || Math.abs(p - page) <= 2)
                      .map((p, idx, arr) => (
                        <React.Fragment key={p}>
                          {idx > 0 && arr[idx - 1] !== p - 1 && (
                            <span className="text-xs text-slate-300 px-1">…</span>
                          )}
                          <button
                            onClick={() => setPage(p)}
                            className={`h-7 min-w-[1.75rem] rounded-md text-[11px] font-medium transition-all ${
                              p === page
                                ? 'bg-blue-600 text-white shadow-sm'
                                : 'text-slate-600 hover:bg-slate-100'
                            }`}
                          >
                            {p}
                          </button>
                        </React.Fragment>
                      ))}
                    <Button variant="outline" size="sm" className="h-7 w-7 p-0" disabled={page >= totalPages} onClick={() => setPage(page + 1)}>
                      <ChevronRight className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* ══════ MODAL: Cell Actions ══════ */}
      {activeCell && activeCellDraft && (
        <div
          className="fixed inset-0 z-50 bg-slate-950/40 px-4 py-6 backdrop-blur-[3px]"
          onClick={() => setActiveCell(null)}
        >
          <div className="flex min-h-full items-center justify-center">
            <div
              className="flex w-full max-w-2xl max-h-[88vh] flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-[0_20px_56px_rgba(15,23,42,0.2)]"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="border-b border-slate-200 bg-slate-50/80 px-4 py-3">
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-2.5">
                    <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-slate-900 text-xs font-semibold text-white">
                      {(activeCell.empName ?? '?')[0]}
                    </div>
                    <div className="min-w-0">
                      <h3 className="text-sm font-semibold text-slate-900 truncate">{activeCell.empName}</h3>
                      <div className="flex items-center gap-1.5 text-[11px] text-slate-500">
                        <Calendar className="w-3 h-3" />
                        {activeCellDraft.dayLabel}, {activeCellDraft.dateLabel}
                      </div>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => setActiveCell(null)}
                    className="rounded-lg p-1.5 text-slate-400 transition hover:bg-slate-100 hover:text-slate-600"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              </div>

              <div className="grid flex-1 overflow-y-auto lg:grid-cols-[minmax(0,1.3fr)_280px]">
                <div className="p-4 lg:border-r lg:border-slate-100">
                  {/* Текущий статус */}
                  {(() => {
                    const activeShiftPalette = activeCellDraft.selectedShift ? getShiftPalette(activeCellDraft.selectedShift.id) : null;
                    const activeSummaryPalette = activeCellDraft.isDayOff ? dayOffPalette : activeShiftPalette;

                    return (
                      <div className="flex items-center gap-2 rounded-xl border border-slate-200 bg-slate-50/80 px-3 py-2 text-xs">
                        <span
                          className="inline-flex items-center gap-1.5 font-semibold"
                          style={activeSummaryPalette ? { color: activeSummaryPalette.mutedText } : undefined}
                        >
                          {activeCellDraft.isDayOff ? '☀ Выходной' : activeCellDraft.selectedShift ? `● ${activeCellDraft.selectedShift.name}` : '○ Пусто'}
                        </span>
                        {activeCellDraft.selectedShift && !activeCellDraft.isDayOff && (
                          <span className="font-mono" style={{ color: activeShiftPalette?.mutedText ?? '#94a3b8' }}>
                            {activeCellDraft.selectedShift.start_time?.slice(0, 5)}–{activeCellDraft.selectedShift.end_time?.slice(0, 5)}
                          </span>
                        )}
                      </div>
                    );
                  })()}

                  <div className="mt-2.5 text-xs font-semibold text-slate-700">Выбор смены</div>

                  <div className="mt-2.5 space-y-1.5">
                    {shifts.map((s: any) => {
                      const isSel = activeCellDraft.currentShiftId === s.id;
                      const palette = getShiftPalette(s.id) ?? buildShiftPalette(DEFAULT_SHIFT_COLORS[0]);
                      return (
                        <div
                          key={s.id}
                          className={`flex items-center gap-2 rounded-xl border px-3 py-2 transition-all ${
                            isSel
                              ? 'border-blue-300 bg-blue-50 ring-1 ring-blue-200'
                              : 'border-slate-200 bg-white hover:border-slate-300'
                          }`}
                          style={isSel ? {
                            backgroundColor: palette.softBackground,
                            borderColor: palette.accent,
                            boxShadow: `0 0 0 1px ${palette.ring}`,
                          } : undefined}
                        >
                          <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ backgroundColor: palette.accent }} />
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-1.5">
                              <span className="text-[13px] font-semibold text-slate-800 truncate">{s.name}</span>
                              <span className="text-[11px] font-mono" style={{ color: palette.mutedText }}>
                                {s.start_time?.slice(0, 5)}–{s.end_time?.slice(0, 5)}
                              </span>
                            </div>
                          </div>
                          <div className="flex items-center gap-1.5 shrink-0">
                            <button
                              type="button"
                              onClick={() => handlePopoverShiftSelect(String(s.id))}
                              className={`rounded-lg px-2.5 py-1.5 text-[11px] font-semibold transition ${
                                isSel
                                  ? 'bg-blue-600 text-white'
                                  : 'bg-slate-800 text-white hover:bg-slate-700'
                              }`}
                            >
                              День
                            </button>
                            {activeCellDraft.canApplyThroughWeek && (
                              <button
                                type="button"
                                onClick={() => handleApplyShiftThroughWeek(String(s.id))}
                                className="rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-[11px] font-semibold text-slate-600 transition hover:border-blue-300 hover:bg-blue-50"
                              >
                                Неделя ({activeCellDraft.remainingDaysCount})
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                <div className="bg-slate-50/70 p-4">
                  <div className="text-xs font-semibold text-slate-700">Быстрые действия</div>

                  <div className="mt-2.5 space-y-1.5">
                    {/* Day-off marking */}
                    <div
                      className={`rounded-xl border px-3 py-2 transition ${
                        activeCellDraft.isDayOff
                          ? ''
                          : 'border-slate-200 bg-white hover:border-slate-300'
                      }`}
                      style={activeCellDraft.isDayOff ? {
                        backgroundColor: dayOffPalette.softBackground,
                        borderColor: dayOffPalette.border,
                      } : undefined}
                    >
                      <div className="flex items-center gap-2.5">
                        <div
                          className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg text-sm"
                          style={{
                            backgroundColor: dayOffPalette.softBackground,
                            color: dayOffPalette.mutedText,
                          }}
                        >
                          ☀
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="text-xs font-semibold text-slate-800">Назначить выходной</div>
                          <div className="text-[10px] text-slate-400">Пометить день как выходной с причиной</div>
                        </div>
                      </div>
                      <div className="mt-2 flex items-center gap-1.5 pl-[38px]">
                        {activeCellDraft.isDayOff ? (
                          <button
                            type="button"
                            onClick={handlePopoverDayOff}
                            className="rounded-lg px-2.5 py-1 text-[10px] font-semibold text-white transition hover:opacity-90"
                            style={{ backgroundColor: dayOffPalette.accent }}
                          >
                            Отменить
                          </button>
                        ) : (
                          <>
                            <button
                              type="button"
                              onClick={() => handleDayOffSingleDay('выходной')}
                              className="rounded-lg px-2.5 py-1 text-[10px] font-semibold text-white transition hover:opacity-90"
                              style={{ backgroundColor: dayOffPalette.accent }}
                            >
                              Этот день
                            </button>
                            {activeCellDraft.canApplyThroughWeek && (
                              <button
                                type="button"
                                onClick={() => handleApplyDayOffThroughWeek('выходной')}
                                className="rounded-lg border bg-white px-2.5 py-1 text-[10px] font-semibold transition hover:opacity-90"
                                style={{
                                  borderColor: dayOffPalette.border,
                                  color: dayOffPalette.mutedText,
                                }}
                              >
                                Неделя ({activeCellDraft.remainingDaysCount})
                              </button>
                            )}
                            <button
                              type="button"
                              onClick={handlePopoverDayOff}
                              className="rounded-lg border border-slate-200 bg-white px-2.5 py-1 text-[10px] font-semibold text-slate-600 transition hover:border-slate-300 hover:bg-slate-50"
                              title="С причиной"
                            >
                              ...
                            </button>
                          </>
                        )}
                      </div>
                    </div>

                    <button
                      type="button"
                      onClick={handlePopoverExcuse}
                      className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-left transition hover:border-purple-200"
                    >
                      <div className="flex items-center gap-2.5">
                        <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-purple-100 text-purple-600">
                          <ShieldCheck className="w-3.5 h-3.5" />
                        </div>
                        <div>
                          <div className="text-xs font-semibold text-slate-800">Разрешение прихода</div>
                          <div className="text-[10px] text-slate-400">Разрешить опоздание с указанием причины</div>
                        </div>
                      </div>
                    </button>

                    <button
                      type="button"
                      onClick={handlePopoverEarlyLeave}
                      className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-left transition hover:border-amber-200"
                    >
                      <div className="flex items-center gap-2.5">
                        <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-amber-100 text-amber-600">
                          <LogOut className="w-3.5 h-3.5" />
                        </div>
                        <div>
                          <div className="text-xs font-semibold text-slate-800">Разрешение раннего ухода</div>
                          <div className="text-[10px] text-slate-400">Укажите, когда сотрудник может уйти</div>
                        </div>
                      </div>
                    </button>

                    <button
                      type="button"
                      onClick={handlePopoverClear}
                      className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-left transition hover:border-red-200 hover:bg-red-50"
                    >
                      <div className="flex items-center gap-2.5">
                        <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-slate-100 text-slate-500">
                          <X className="w-3.5 h-3.5" />
                        </div>
                        <div>
                          <div className="text-xs font-semibold text-slate-800">Очистить ячейку</div>
                          <div className="text-[10px] text-slate-400">Убрать выбранную смену или выходной</div>
                        </div>
                      </div>
                    </button>
                  </div>

                  <p className="mt-3 rounded-lg border border-dashed border-slate-200 bg-white/80 px-3 py-2 text-[10px] text-slate-400 leading-4">
                    Не забудьте нажать «Сохранить».
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ══════ MODAL: Copy Employee Schedule ══════ */}
      {copyModal?.open && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={(e) => { if (e.target === e.currentTarget) setCopyModal(null); }}>
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg mx-4 overflow-hidden animate-in fade-in zoom-in-95 duration-200">
            <div className="bg-blue-50 px-5 py-3 border-b flex items-center justify-between">
              <div>
                <h3 className="text-base font-semibold text-blue-900">Копирование графика</h3>
                <p className="text-xs text-blue-600">{copyModal.sourceRow.employee_name} → другим сотрудникам</p>
              </div>
              <button className="text-slate-400 hover:text-slate-600" onClick={() => setCopyModal(null)}>
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Source preview */}
            <div className="px-5 py-3 border-b bg-slate-50/50">
              <p className="text-[10px] font-medium text-slate-400 uppercase tracking-wider mb-1.5">Исходный график:</p>
              <div className="flex gap-1">
                {dates.map((d: any, i: number) => {
                  const dateStr = typeof d === 'string' ? d : d.date;
                  const key = `${copyModal.sourceRow.employee_id}|${dateStr}`;
                  const change = localChanges[key];
                  const cell = copyModal.sourceRow.days?.[dateStr] ?? {};
                  const isDayOff = change !== undefined ? Boolean(change.is_day_off) : Boolean(cell.is_day_off);
                  const shiftId = change !== undefined ? change.shift_id : (cell.shift_id ?? null);
                  const shift = shiftId ? shifts.find((s: any) => s.id === Number(shiftId)) : null;
                  return (
                    <div
                      key={i}
                      className="flex-1 rounded border px-1 py-1 text-center text-[9px] font-semibold"
                      style={isDayOff ? {
                        backgroundColor: dayOffPalette.softBackground,
                        borderColor: dayOffPalette.border,
                        color: dayOffPalette.mutedText,
                      } : shift ? {
                        backgroundColor: getShiftPalette(shift.id)?.softBackground,
                        borderColor: getShiftPalette(shift.id)?.border,
                        color: getShiftPalette(shift.id)?.text,
                      } : {
                        backgroundColor: emptyPalette.softBackground,
                        borderColor: emptyPalette.border,
                        color: emptyPalette.mutedText,
                      }}
                    >
                      <div className="font-bold">{DAY_NAMES[new Date(dateStr + 'T00:00:00').getDay()]}</div>
                      <div>{isDayOff ? 'ВЫХ' : shift ? shift.name : '—'}</div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="px-5 py-3 space-y-2 max-h-[45vh] overflow-y-auto">
              {/* Search in modal */}
              <div className="relative mb-1">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
                <input
                  type="text"
                  placeholder="Поиск..."
                  value={copySearch}
                  onChange={(e) => setCopySearch(e.target.value)}
                  className="w-full h-8 pl-8 pr-3 rounded-lg border border-slate-200 text-xs focus:outline-none focus:ring-2 focus:ring-blue-400"
                />
              </div>

              {/* Select all */}
              <label className="flex items-center gap-2 text-xs font-medium text-slate-700 pb-1.5 border-b">
                <input
                  type="checkbox"
                  className="h-3.5 w-3.5 rounded accent-blue-600"
                  checked={(() => {
                    const otherIds = rows.filter((r: any) => r.employee_id !== copyModal.sourceRow.employee_id && (!copySearch || (r.employee_name ?? '').toLowerCase().includes(copySearch.toLowerCase()))).map((r: any) => r.employee_id);
                    return otherIds.length > 0 && otherIds.every((id: number) => copyTargets.has(id));
                  })()}
                  onChange={() => {
                    const otherIds = rows.filter((r: any) => r.employee_id !== copyModal.sourceRow.employee_id && (!copySearch || (r.employee_name ?? '').toLowerCase().includes(copySearch.toLowerCase()))).map((r: any) => r.employee_id);
                    setCopyTargets(prev => {
                      const allSelected = otherIds.every((id: number) => prev.has(id));
                      if (allSelected) { const next = new Set(prev); otherIds.forEach((id: number) => next.delete(id)); return next; }
                      const next = new Set(prev); otherIds.forEach((id: number) => next.add(id)); return next;
                    });
                  }}
                />
                Выбрать всех
              </label>

              {rows
                .filter((r: any) => r.employee_id !== copyModal.sourceRow.employee_id && (!copySearch || (r.employee_name ?? '').toLowerCase().includes(copySearch.toLowerCase())))
                .map((r: any) => (
                <label key={r.employee_id} className="flex items-center gap-2.5 text-xs cursor-pointer hover:bg-slate-50 rounded-lg px-2 py-1.5 -mx-2 transition">
                  <input
                    type="checkbox"
                    className="h-3.5 w-3.5 rounded accent-blue-600"
                    checked={copyTargets.has(r.employee_id)}
                    onChange={() => setCopyTargets(toggleSet(copyTargets, r.employee_id))}
                  />
                  <div className="w-6 h-6 rounded-full bg-slate-200 flex items-center justify-center text-[10px] font-bold text-slate-600">
                    {(r.employee_name ?? '?')[0]}
                  </div>
                  <span className="text-slate-800">{r.employee_name}</span>
                  {r.employee_code && <span className="text-[10px] text-slate-400 font-mono">#{r.employee_code}</span>}
                </label>
              ))}
            </div>
            <div className="flex items-center justify-between px-5 py-3 border-t bg-slate-50">
              <span className="text-xs text-slate-500">{copyTargets.size} выбр.</span>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => setCopyModal(null)}>Отмена</Button>
                <Button size="sm" onClick={handleCopyEmployee} disabled={copyTargets.size === 0}>
                  <Copy className="w-3.5 h-3.5 mr-1" />
                  Копировать
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ══════ MODAL: Bulk Column Apply ══════ */}
      {bulkColumnModal?.open && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={(e) => { if (e.target === e.currentTarget) setBulkColumnModal(null); }}>
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm mx-4 overflow-hidden animate-in fade-in zoom-in-95 duration-200">
            <div className="bg-emerald-50 px-5 py-3 border-b flex items-center justify-between">
              <div>
                <h3 className="text-base font-semibold text-emerald-900">{bulkColumnModal.dayName} — всем</h3>
                <p className="text-xs text-emerald-600">Одна смена для {filteredRows.length} сотр.</p>
              </div>
              <button className="text-slate-400 hover:text-slate-600" onClick={() => setBulkColumnModal(null)}>
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-5 space-y-4">
              <div className="flex items-center gap-3">
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input
                    type="radio"
                    name="bulk-type"
                    className="accent-blue-600"
                    checked={!bulkIsDayOff}
                    onChange={() => setBulkIsDayOff(false)}
                  />
                  Смена
                </label>
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input
                    type="radio"
                    name="bulk-type"
                    className="accent-orange-500"
                    checked={bulkIsDayOff}
                    onChange={() => { setBulkIsDayOff(true); setBulkShiftId(''); }}
                  />
                  ☀ Выходной
                </label>
              </div>

              {!bulkIsDayOff && (
                <div>
                  <label className="text-xs font-medium text-slate-600 block mb-1">Выберите смену</label>
                  <div className="grid grid-cols-1 gap-1.5">
                    {shifts.map((s: any) => (
                      (() => {
                        const palette = getShiftPalette(s.id) ?? buildShiftPalette(DEFAULT_SHIFT_COLORS[0]);
                        const isSelected = bulkShiftId === String(s.id);

                        return (
                      <button
                        key={s.id}
                        onClick={() => setBulkShiftId(String(s.id))}
                        className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-left text-sm transition ${
                          isSelected ? 'shadow-sm' : 'bg-white hover:border-slate-300'
                        }`}
                        style={{
                          backgroundColor: isSelected ? palette.softBackground : '#ffffff',
                          borderColor: isSelected ? palette.accent : '#e2e8f0',
                          boxShadow: isSelected ? `0 0 0 2px ${palette.ring}` : undefined,
                          color: palette.text,
                        }}
                      >
                        <span className="h-3 w-3 rounded-full" style={{ backgroundColor: palette.accent }} />
                        <span className="font-medium">{s.name}</span>
                        <span className="text-xs" style={{ color: palette.mutedText }}>{s.start_time?.slice(0, 5)}–{s.end_time?.slice(0, 5)}</span>
                        {bulkShiftId === String(s.id) && <Check className="w-4 h-4 ml-auto" />}
                      </button>
                        );
                      })()
                    ))}
                    <button
                      onClick={() => setBulkShiftId('')}
                      className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-sm text-left transition ${
                        bulkShiftId === '' && !bulkIsDayOff
                          ? 'bg-slate-100 border-slate-300 ring-2 ring-slate-300'
                          : 'border-slate-200 hover:border-slate-300 bg-white'
                      }`}
                    >
                      <span className="w-3 h-3 rounded-full bg-slate-300" />
                      <span className="text-slate-500">Очистить (убрать смену)</span>
                    </button>
                  </div>
                </div>
              )}
            </div>
            <div className="flex justify-end gap-2 px-5 py-3 border-t bg-slate-50">
              <Button variant="outline" size="sm" onClick={() => setBulkColumnModal(null)}>Отмена</Button>
              <Button size="sm" onClick={handleBulkColumnApply}>
                <ArrowRight className="w-3.5 h-3.5 mr-1" />
                Применить ({filteredRows.length} сотр.)
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* ══════ MODAL: Day Off Reason ══════ */}
      {dayOffModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={(e) => { if (e.target === e.currentTarget) setDayOffModal(null); }}>
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md mx-4 overflow-hidden">
            <div
              className="flex items-center justify-between border-b px-5 py-3"
              style={{
                backgroundColor: dayOffPalette.softBackground,
                borderColor: dayOffPalette.border,
              }}
            >
              <div>
                <h3 className="text-base font-semibold" style={{ color: dayOffPalette.text }}>Назначение выходного</h3>
                <p className="text-xs" style={{ color: dayOffPalette.mutedText }}>{dayOffModal.empName} — {dayOffModal.date}</p>
              </div>
              <button className="text-slate-400 hover:text-slate-600" onClick={() => setDayOffModal(null)}>
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-5 space-y-4">
              <div
                className="rounded-xl border px-4 py-3 text-sm"
                style={{
                  backgroundColor: dayOffPalette.softBackground,
                  borderColor: dayOffPalette.border,
                  color: dayOffPalette.mutedText,
                }}
              >
                Выбранный день будет помечен как выходной. Если укажете причину, она будет видна при просмотре.
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Причина</label>
                <textarea
                  className="w-full rounded-xl border border-gray-300 px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 min-h-[88px]"
                  placeholder="Например: семейные обстоятельства, визит к врачу, поездка..."
                  value={dayOffReason}
                  onChange={(e) => setDayOffReason(e.target.value)}
                />
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setDayOffModal(null)}>Отмена</Button>
                <Button onClick={handleSaveDayOff}>Сохранить</Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ══════ MODAL: Excuse ══════ */}
      {excuseModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={(e) => { if (e.target === e.currentTarget) setExcuseModal(null); }}>
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md mx-4 overflow-hidden">
            <div className="bg-purple-50 px-5 py-3 border-b flex items-center justify-between">
              <div>
                <h3 className="text-base font-semibold text-purple-900">Разрешение прихода</h3>
                <p className="text-xs text-purple-600">{excuseModal.empName} — {excuseModal.date}</p>
              </div>
              <button className="text-slate-400 hover:text-slate-600" onClick={() => setExcuseModal(null)}>
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Разрешить приход до (время)</label>
                <Input type="time" value={excuseForm.excused_until} onChange={(e) => setExcuseForm({ ...excuseForm, excused_until: e.target.value })} />
                <p className="text-xs text-slate-400 mt-1">Например: 11:00 — может прийти до этого времени</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Причина</label>
                <textarea
                  className="w-full rounded-xl border border-gray-300 px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 min-h-[70px]"
                  placeholder="Визит к врачу, медосмотр..."
                  value={excuseForm.excuse_reason}
                  onChange={(e) => setExcuseForm({ ...excuseForm, excuse_reason: e.target.value })}
                />
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setExcuseModal(null)}>Отмена</Button>
                <Button onClick={handleSaveExcuse} disabled={excuseLoading}>
                  {excuseLoading ? 'Сохранение…' : 'Сохранить'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Early leave excuse modal */}
      {earlyLeaveModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={(e) => { if (e.target === e.currentTarget) setEarlyLeaveModal(null); }}>
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md mx-4 overflow-hidden">
            <div className="bg-orange-50 px-5 py-3 border-b flex items-center justify-between">
              <div>
                <h3 className="text-base font-semibold text-orange-900">Разрешение раннего ухода</h3>
                <p className="text-xs text-orange-600">{earlyLeaveModal.empName} — {earlyLeaveModal.date}</p>
              </div>
              <button className="text-slate-400 hover:text-slate-600" onClick={() => setEarlyLeaveModal(null)}>
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-5 space-y-4">
              {earlyLeaveModal.hasExisting && (
                <div className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-700">
                  Текущее разрешение уже сохранено. Его можно обновить или удалить.
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Может уйти с (время)</label>
                <Input type="time" value={earlyLeaveForm.early_leave_excused_until} onChange={(e) => setEarlyLeaveForm({ ...earlyLeaveForm, early_leave_excused_until: e.target.value })} />
                <p className="text-xs text-slate-400 mt-1">Например: 16:00 — может уйти после этого времени</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Причина</label>
                <textarea
                  className="w-full rounded-xl border border-gray-300 px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 min-h-[70px]"
                  placeholder="Забрать ребёнка, визит к врачу..."
                  value={earlyLeaveForm.early_leave_excuse_reason}
                  onChange={(e) => setEarlyLeaveForm({ ...earlyLeaveForm, early_leave_excuse_reason: e.target.value })}
                />
              </div>
              <div className="flex justify-end gap-2">
                {earlyLeaveModal.hasExisting && (
                  <Button variant="destructive" onClick={handleRemoveEarlyLeaveExcuse} disabled={earlyLeaveLoading}>
                    Удалить разрешение
                  </Button>
                )}
                <Button variant="outline" onClick={() => setEarlyLeaveModal(null)}>Отмена</Button>
                <Button onClick={handleSaveEarlyLeaveExcuse} disabled={earlyLeaveLoading}>
                  {earlyLeaveLoading ? 'Сохранение…' : 'Сохранить'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
